import { useState, useEffect, FC, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { BgColorsOutlined, CopyOutlined, DownloadOutlined, LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Empty, Input, message, Modal, Radio, Space, Spin, Tooltip } from 'antd';
import { Properties } from 'csstype';

import 'react-base-table/styles.css';
import moment from 'moment';
import BaseTable from 'react-base-table';
import { v4 as uuidv4 } from 'uuid';

import { SPARKLINE_INDENT, SPARKLINE_WIDTH, Sparkline, SparklineLoading } from './Sparkline';
import {
    buildAnalysisRowKey,
    calcMetricChange,
    formatAnalysisPeriod,
    formatMetricChange,
    getMetricTrendDirection,
    getPreviousPeriod,
    getTrendTone,
    isPercentMetric,
    parseMetricValue,
} from '../../../../../helpers/utils/periodAnalysis';
import { safeLocalStorage } from '../../../../../helpers/utils/safeStorage';

import {
    ReportTableProps,
    ReportType,
    MetricItem,
    GroupingItem,
    TableRowData,
    MaxValuesMap,
    CellRenderProps,
    ColumnConfig,
    COL_GROUP_MINWIDTH,
    COL_WIDTH,
    COL_MIN_WIDTH,
    COL_MAX_WIDTH,
    HEADER_HEIGHT,
    TABLE_MIN_WIDTH,
    TABLE_MIN_HEIGHT,
    TABLE_HEIGHT_OFFSET,
    EXPAND_DELAY_MS,
    CELL_HEIGHT,
    DEBOUNCE_RESIZE_MS,
    TOTAL_PATH_STR,
    TOTAL_CHOSEN_PATH_STR,
    TOTAL_LABEL,
    EMPTY_GROUP,
} from './types';
import { getReportConfig } from './config';
import { copyAllTableData as copyAllData, buildAllTableDataText, showCopyResult } from './utils';
import { copyToClipboardDeferred } from '../../../../../helpers/utils/safeClipboard';
import ExpandIcon from '../un-table/components/ExpandIcon';
import GroupCell from '../un-table/components/GroupCell';
import GroupHeader from '../un-table/components/GroupHeader';
import HeaderCell from '../un-table/components/HeaderCell';
import { findObjectById, formatPeriodDate, getSummaryOfGroupings, CustomMetricsMap } from '../../../../../helpers/utils/functions';
import { getAxisGroupForMetric, getAxisGroupForCustomMetric } from '../charts/MetricAxisGroups';
import {
    checkConvertDataType,
    checkMoneyDataType,
    formatConvertData,
    formatMoneyData,
    finishRows,
    prepareRows,
    checkPercentDataType,
    buildGroupingsNames,
    buildDisplayGroupings,
    resolveGroupAxisFilter,
    groupSuffixByReportType,
} from '../../../../../helpers/utils/reports';
import { getCellFillStyle, getCustomMetricRow } from '../../../../../helpers/utils/tableHelpers';
import type { CellFillSettings } from '../../../../../helpers/utils/tableHelpers';
import { showErrorMessage } from '../../../../../helpers/utils/ui';
import { isDeletedMetricsError, handleDeletedMetricsError } from '../../../../../helpers/utils/deletedMetrics';
import { isDeletedGroupsError, handleDeletedGroupsError } from '../../../../../helpers/utils/deletedGroups';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { findTopParentObjectName } from '../../../constants/reports';
import { useActions } from '../../../hooks/useActions';
import { useDidComponentUpdate } from '../../../hooks/useDidComponentUpdate';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import { useDebounce } from '../../../hooks/useDebounce';
import { useTimerCleanup } from '../../../hooks/useTimerCleanup';
import { useDashboardBlockSettings } from '../../../hooks/useDashboardBlockSettings';
import { buildApiFilters, mergeAndBuildApiFilters } from '../params/filter/apiFilters';
import { validateTableRequest, devLog } from '../../../../../helpers/utils/reportValidation';
import { isAbortError } from '../../../../../helpers/utils/reportApiUtils';

// «Динамика» — рост/падение метрик к прошлому периоду. Рабочее название кнопки, меняется здесь
const ANALYSIS_BUTTON_NAME = 'Динамика';

/**
 * Значок кнопки «Динамика» — две стрелки наискось: вверх-вправо (рост) и вниз-вправо (падение).
 * Выбран Татьяной и Денисом из вариантов (№9). Не подошли: RiseOutlined — как «Показать график»,
 * вертикальные стрелки — как сортировка, знак процента — занят кнопкой «%» столбца, часы — непонятно
 */
const DynamicsIcon: FC = () => (
    <span role="img" className="anticon">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            {/* Стрелки короче и дальше друг от друга: между ними ~7 из 24, чтобы не слипались */}
            <path d="M1.5 15.5l7-7" />
            <path d="M4 8.5h4.5V13" />
            <path d="M15.5 8.5l7 7" />
            <path d="M22.5 11v4.5H18" />
        </svg>
    </span>
);
// Зазор между подписью и числом — column-gap у .report-analysis-cell (table.scss)
const ANALYSIS_BADGE_GAP = 8;

interface AnalysisData {
    /** Строки прошлого периода по ключу пути группировок (buildAnalysisRowKey) */
    rows: Record<string, TableRowData>;
    /** Уровни, уже загруженные за прошлый период: '' — верхний, иначе путь родителя */
    loadedParents: Record<string, true>;
    /** «Итого и среднее» за прошлый период */
    total: Record<string, unknown> | null;
    /** Даты прошлого периода, за который загружены данные — для подсказки */
    periodLabel: string;
}

const EMPTY_ANALYSIS_DATA: AnalysisData = { rows: {}, loadedParents: {}, total: null, periodLabel: '' };

// Спарклайны: колонок в одном запросе (как FilterSeries::MAX_METRICS на бэке) — больше колонок идут
// несколькими запросами по столько же; и строк уровня за один запрос
const SPARKLINE_METRICS_PER_REQUEST = 6;
const SPARKLINE_MAX_ROWS = 50;
// Строк уровня всего (порциями по SPARKLINE_MAX_ROWS): тысячи UTM-меток не гоним на сервер целиком
const SPARKLINE_MAX_LEVEL_ROWS = 500;
// Запросов линий одновременно: после «Раскрыть всё» уровней много, а каждый запрос тяжёлый
const SPARKLINE_MAX_PARALLEL = 2;
interface SparkData {
    /** Значения по отрезкам дат: ключ строки (buildAnalysisRowKey) → метрика → значения */
    rows: Record<string, Record<string, Array<number | null>>>;
    /** «Итого и среднее» по тем же отрезкам */
    total: Record<string, Array<number | null>> | null;
    /** Подписи отрезков: «01.09.2026», «Сентябрь 2026» */
    labels: string[];
    /** Детализация, по которой построены линии (сервер укрупняет до 60 точек) */
    details: string | null;
    /** Строки (ключ как у rows), чьи линии запрошены и ещё не пришли — в ячейках заглушка «грузится» */
    pending: Record<string, true>;
    /** Линия «Итого» ещё грузится */
    totalPending: boolean;
}

const withoutKeys = (source: Record<string, true>, keys: string[]): Record<string, true> => {
    const next = { ...source };
    keys.forEach((key) => { delete next[key]; });
    return next;
};

// Адрес отчёта по типу таблицы — для выгрузки с дашборда (как DATE_TO_PATHNAME в окне блока: визиты — универсальный)
const REPORT_TYPE_PATHNAME: Record<ReportType, string> = {
    universal: '/users/universal',
    order: '/users/order-reports',
    payment: '/users/payment-reports',
    ad: '/users/ad-reports',
    webinar: '/users/webinar-reports',
    visit: '/users/universal',
};

const EMPTY_SLOT_TABLE_DATA ={ data: [], total: {}, totalChosen: { active: [], metrics: {} } };

const EMPTY_SPARK_DATA: SparkData = { rows: {}, total: null, labels: [], details: null, pending: {}, totalPending: false };
const EMPTY_CELL_FILLS: Record<string, CellFillSettings> = {};


export const ReportTable: FC<ReportTableProps> = ({ 
    reportType, 
    hidePersonalDataGroups,
    blockId,
    displayMode = 'full',
    isModal = false,
    date,
    maxRows,
    onOpenFullReport,
    hideGroupingsPanel = false,
    hideMetricsPanel = false,
    tableHeight,
    hideSelectionControls = false,
    hideTotalCheckbox = false,
    hideMetricButtons = false,
    hideTotalChosenRow = false,
    hideRowCheckboxes = false,
    disableGroupsLimit = false,
    overridePeriod,
    blockContainerWidth,
    toolbarSlots,
    showTableSettings = false,
    getExportFileName,
    dataSlotId,
}) => {
    const config = getReportConfig(reportType);
    const { GroupingsComponent, MetricsComponent, tableId, groupingsTooltip, metricsTooltip, tableTooltip, hasAdsGroups } = config;

    // Хук для безопасной работы с таймерами
    const { safeSetTimeout } = useTimerCleanup();

    // Состояние для принудительного обновления (замена forceUpdate)
    const [updateTrigger, setUpdateTrigger] = useState(0);
    const triggerUpdate = useCallback(() => setUpdateTrigger(prev => prev + 1), []);
    
    // Определяем контекст использования
    // isDashboardBlock: блок дашборда (compact режим) ИЛИ модальное окно с blockId (full режим для просмотра блока)
    const isDashboardBlock = !!blockId && (displayMode !== 'full' || (isModal && displayMode === 'full'));
    const isCompactMode = displayMode === 'compact' || displayMode === 'preview';
    // Флаг: модальное окно для просмотра блока дашборда (FullReportModal)
    const isBlockViewModal = isModal && !!blockId && displayMode === 'full';
    // Флаг: модальное окно редактирования/добавления/копирования блока (preview режим без blockId)
    const isEditingModal = isModal && !blockId && displayMode === 'preview';
    
    // Предпросмотр окна блока: при закрытии окна его строки больше не нужны — при следующем
    // открытии не должны мелькать данные прошлого блока
    const { setTableData: clearSlotTableData } = useActions();
    useEffect(() => () => {
        if (dataSlotId) {
            clearSlotTableData({ rows: [], total: {}, groupings: [], blockId: dataSlotId });
        }
    }, [ dataSlotId ]);

    // Окно блока: своя загрузка этой таблицы (для заставки). Общий флаг загрузки meta
    // переключают обе таблицы окна (редактор и предпросмотр), и он снимался по первому ответу
    const [ ownTableLoading, setOwnTableLoading ] = useState(false);
    const ownLoadSeqRef = useRef(0);
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    // Получаем настройки блока через централизованный хук
    const blockSettings = useDashboardBlockSettings(blockId, { 
        isModal: isModal && !isBlockViewModal, // Для FullReportModal используем настройки блока, не модальные
        skip: !blockId 
    });

    const globalPeriod = useTypedSelector((state) => state.period);
    // Используем переопределённый период если передан, иначе глобальный
    const period = overridePeriod || globalPeriod;
    
    // Атрибуты: для блоков дашборда используем blockSettings
    const attributes = useTypedSelector((state) => {
        if (isDashboardBlock) {
            return {
                attribution: blockSettings.attribution,
                type_applications: blockSettings.type_applications,
            };
        }
        if (isModal) {
            return state.attributes.attributes;
        }
        return state.attributes.attributes;
    });
    
    // Фильтры: для блоков используем блочные, для модалок — dashboardModal
    const { filters } = useTypedSelector((state) => {
        if (isDashboardBlock && blockId) {
            return state.filter.blocks[blockId] ?? { filters: [] };
        }
        if (isModal) {
            return state.filter.dashboardModal;
        }
        return state.filter;
    });
    
    // Общие фильтры дашборда (для мержа с блочными)
    // Используем blockSettings.globalFilters напрямую — он уже реактивно обновляется через useDashboardBlockSettings
    const commonFilters = isDashboardBlock ? blockSettings.globalFilters : [];
    // Общие фильтры дашборда для выгрузки из окна блока (у самого окна фильтры только блочные)
    const dashboardFilters = useTypedSelector((state) => state.filter.filters);
    // Атрибуция для выгрузки/копирования из окна блока — как у блока на дашборде: атрибуция
    // дашборда, а своя у блока (атрибуция окна) — только если у дашборда «По умолчанию» ('blocks')
    const attributesDashboard = useTypedSelector((state) => state.attributes.attributesDashboard);
    const dashboardBlockAttributes = useMemo(() => {
        const isBlockAttribution = attributesDashboard?.attribution?.[0] === 'blocks';
        const isBlockTypeApplications = attributesDashboard?.type_applications?.[0] === 'blocks';
        return {
            attribution: isBlockAttribution || !attributesDashboard?.attribution
                ? attributes?.attribution
                : attributesDashboard.attribution,
            type_applications: isBlockTypeApplications || !attributesDashboard?.type_applications
                ? attributes?.type_applications
                : attributesDashboard.type_applications,
        };
    }, [ attributesDashboard, attributes ]);
    
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    
    // Мета-состояние: для блоков используем блочный state
    const {
        isCompare,
        isTableLoading,
        isChartLoading,
        isTableFetching,
        isChartFetching,
        isModalTableFetching,
        currency,
        isAllExpandedLoading,
        isTableCompareFetching,
        isNotParamsFilterVisible,
        skipTableFetchUntil,
    } = useTypedSelector((state) => {
        if (blockId) {
            const blockMeta = state.meta.blocks[blockId];
            return {
                isCompare: blockMeta?.isCompare ?? state.meta.isCompare,
                isTableLoading: blockMeta?.isTableLoading ?? state.meta.isTableLoading,
                isChartLoading: blockMeta?.isChartLoading ?? state.meta.isChartLoading,
                isTableFetching: blockMeta?.isTableFetching ?? state.meta.isTableFetching,
                isChartFetching: blockMeta?.isChartFetching ?? state.meta.isChartFetching,
                isModalTableFetching: blockMeta?.isModalTableFetching ?? state.meta.isModalTableFetching,
                currency: state.meta.currency,
                isAllExpandedLoading: state.meta.isAllExpandedLoading,
                isTableCompareFetching: blockMeta?.isTableCompareFetching ?? state.meta.isTableCompareFetching,
                isNotParamsFilterVisible: state.meta.isNotParamsFilterVisible,
                skipTableFetchUntil: state.meta.skipTableFetchUntil,
            };
        }
        return state.meta;
    });
    
    // Для модальных окон используем isModalTableFetching вместо isTableFetching
    const effectiveTableFetching = isModal && !blockId ? isModalTableFetching : isTableFetching;
    
    // Используем ref для isNotParamsFilterVisible, чтобы изменение флага не триггерило useEffect
    const isNotParamsFilterVisibleRef = useRef(isNotParamsFilterVisible);
    isNotParamsFilterVisibleRef.current = isNotParamsFilterVisible;
    
    // Используем ref для disableGroupsLimit, чтобы изменение пропа не триггерило useEffect
    const disableGroupsLimitRef = useRef(disableGroupsLimit);
    disableGroupsLimitRef.current = disableGroupsLimit;
    
    // Данные графика: для блоков используем блочный state
    const { chartType } = useTypedSelector((state) => {
        if (isDashboardBlock && blockId) {
            return state.chart.blocks[blockId] ?? state.chart;
        }
        return state.chart;
    });
    
    // Данные таблицы: для блоков используем блочный state, для модальных окон — глобальный
    // ВАЖНО: для модальных окон редактирования (isEditingModal) используем глобальный state.table,
    // т.к. данные записываются туда через setMetrics/setGroupings
    const {
        groupings: rawGroupings,
        metrics: rawMetrics,
        tableData: sharedTableData,
        percentages,
        sparklines: rawSparklines,
        cellFills: rawCellFills,
        analysis: rawAnalysis,
        cellFilling: rawCellFilling,
        filtersTable,
        sortTable,
        groupsChecked,
        metricsChecked,
        totalChecked,
        groupsSelectionMode,
        selectFirstNRows,
        frozensColumns,
        metricsLimit,
        groupsLimit,
        columnWidth: savedColumnWidth,
        columnsWidth: savedColumnsWidth,
    } = useTypedSelector((state) => {
        // Для модальных окон редактирования используем глобальный state
        if (isEditingModal) {
            return state.table;
        }
        // Для блоков дашборда используем блочный state
        if (isDashboardBlock && blockId) {
            return state.table.blocks[blockId] ?? state.table;
        }
        return state.table;
    });

    // Строки таблицы: у предпросмотра окна блока — своё место (dataSlotId), настройки общие
    const slotTableData = useTypedSelector((state) => (dataSlotId ? state.table.blocks[dataSlotId]?.tableData : undefined));
    const tableData = dataSlotId ? (slotTableData ?? EMPTY_SLOT_TABLE_DATA) : sharedTableData;
    // Куда писать загруженные строки
    const dataBlockId = blockId ?? dataSlotId;

    // Фильтруем undefined элементы и элементы без dataKey для защиты от ошибок
    // Используем useMemo чтобы избежать создания нового массива при каждом рендере
    const filteredGroupings = useMemo(() => (rawGroupings ?? []).filter((g: any) => g && g.dataKey), [rawGroupings]);
    const filteredMetrics = useMemo(() => (rawMetrics ?? []).filter((m: any) => m && m.dataKey), [rawMetrics]);
    
    // Fallback: если groupings/metrics пустые, но groupsChecked/metricsChecked не пустые,
    // используем их как источник данных (для совместимости со старыми блоками дашборда)
    const groupings = useMemo(() => {
        if (filteredGroupings.length > 0) return filteredGroupings;
        if (isDashboardBlock && groupsChecked && groupsChecked.length > 0) {
            return groupsChecked.filter((g: any) => g && g.dataKey);
        }
        return filteredGroupings;
    }, [filteredGroupings, isDashboardBlock, groupsChecked]);

    // Фильтр по группам регистраций (app_custom_groups / not_app_custom_groups) из
    // текущего скоупа: приводит ось группировок к фильтру (как на бэке), чтобы
    // отправляемая и отображаемая глубина совпадали.
    const groupFilter = useMemo(() => resolveGroupAxisFilter(filters), [filters]);

    // Схлопнутые уровни для отображения: набор групп регистрации = один уровень
    // «Группы регистраций». Индекс уровня в пути строки соответствует этому списку.
    const displayGroupings = useMemo(() => buildDisplayGroupings(groupings, groupFilter), [groupings, groupFilter]);

    const metrics = useMemo(() => {
        if (filteredMetrics.length > 0) return filteredMetrics;
        if (isDashboardBlock && metricsChecked && metricsChecked.length > 0) {
            return metricsChecked.filter((m: any) => m && m.dataKey);
        }
        return filteredMetrics;
    }, [filteredMetrics, isDashboardBlock, metricsChecked]);
    
    const { compareFilters, comparePeriod } = useTypedSelector(
        (state) => state.compare
    );
    const {
        setMetaTableLoading,
        setGroupsLimit,
        setMetricsLimit,
        setTableData,
        setTableChildrenData,
        setMetaCompareTableFetching,
        setMetaTableFetching,
        setMetaModalTableFetching,
        setTotalChecked,
        setMetricsChecked,
        setTotalChosenMetrics,
        setTotalChosenStatus,
        getCreatedMetrics,
        getCustomGroups,
        getUtmSections,
        setIsAllExpandedLoading,
        clearTotalChecked,
        setAllBlocksFetching,
        setMetaFilterUpdate,
        setAllDataFetching,
        setGroupingsChecked,
        setGroupsSelectionMode,
        setFrozensColumns: setFrozensColumnsAction,
        loadBlockTableData,
        setTableAnalysis,
        setTableCellFilling,
    } = useActions();

    // Блоки дашборда (и окно блока) хранят «Динамику» и заливку всей таблицы в Redux, чтобы они
    // сохранялись с дашбордом. В отчётах — как раньше, только внутри таблицы
    // «Полный отчёт» блока (isBlockViewModal) — только посмотреть, блок не меняет: там локально, как в отчёте
    const storesTableToggles = isEditingModal || (isDashboardBlock && !!blockId && !isBlockViewModal);
    const saveTableToggle = useCallback((setter: (payload: boolean | { blockId: string; data: boolean }) => unknown, value: boolean) => {
        if (isEditingModal) {
            setter(value);
        } else if (blockId) {
            setter({ blockId, data: value });
        }
    }, [ isEditingModal, blockId ]);

    // Обёртка для setFrozensColumns, чтобы работать как функциональный setter
    // Добавляем защиту от undefined frozensColumns
    const safeFrozensColumns = frozensColumns ?? {};
    const setFrozensColumns = useCallback((updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
        if (typeof updater === 'function') {
            setFrozensColumnsAction(updater(safeFrozensColumns));
        } else {
            setFrozensColumnsAction(updater);
        }
    }, [setFrozensColumnsAction, safeFrozensColumns]);

    // Типизированные состояния
    const [dimensionsTable, setDimensionsTable] = useState({ height: 0 });
    const dataRef = useRef<{ data: TableRowData[] }>({ data: [] });
    const abortControllerRef = useRef<AbortController | null>(null);
    const compareAbortControllerRef = useRef<AbortController | null>(null);
    const currentReportTypeRef = useRef<string>(reportType);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const downloadedRowKeysSet = useRef<Set<string>>(new Set()); // Set вместо массива
    const loadRowKeysSet = useRef<Set<string>>(new Set()); // Set для быстрого lookup
    const [loadRowKeys, setLoadRowKeys] = useState<string[]>([]);
    const [tableWidth, setTableWidth] = useState(1200);
    const [columnsTable, setColumnsTable] = useState<ColumnConfig[]>([]);
    // В compact-режиме: ширина = ширина блока-контейнера (горизонтальный скролл внутри body BaseTable)
    const effectiveTableWidth = useMemo(() => {
        if (!isCompactMode || columnsTable.length === 0) return tableWidth;
        return blockContainerWidth || tableWidth;
    }, [isCompactMode, tableWidth, blockContainerWidth, columnsTable]);
    const [adsGroupsSelected, setAdsGroupsSelected] = useState(false);
    const [adsGroupsAvailable, setAdsGroupsAvailable] = useState(false);
    const [didUpdate, handleUpdate] = useDidComponentUpdate();
    const [expandedAll, setExpandedAll] = useState<string[]>([]);
    const expandedAllSet = useRef<Set<string>>(new Set()); // Set для быстрого lookup
    const [ localFilling, setLocalFilling ] = useState(false);
    const isFilling = storesTableToggles ? rawCellFilling === true : localFilling;
    const toggleFilling = () => {
        if (storesTableToggles) {
            saveTableToggle(setTableCellFilling, !isFilling);
        } else {
            setLocalFilling((prev) => !prev);
        }
    };
    const [flatMode, setFlatMode] = useState(false);
    const flatModeRef = useRef(false);
    const tableDivRef = useRef<HTMLDivElement>(null);

    // «Динамика»: данные за прошлый период хранятся отдельно, данные таблицы в Redux не меняются
    const [ isAnalysis, setIsAnalysis ] = useState(false);
    const [ analysisLoading, setAnalysisLoading ] = useState(false);
    const [ analysisData, setAnalysisData ] = useState<AnalysisData>(EMPTY_ANALYSIS_DATA);
    // Доп. ширина колонок, где подпись и число не помещаются рядом: dataKey → px
    const [ analysisExtraWidth, setAnalysisExtraWidth ] = useState<Record<string, number>>({});
    const isAnalysisRef = useRef(false);
    isAnalysisRef.current = isAnalysis;
    // Ячейки читают данные прошлого периода из ref в момент отрисовки: BaseTable не подменяет
    // колонки, если у них изменились только функции (ignoreFunctionInColumnCompare), и
    // cellRenderer с замыканием на state так и остался бы со старыми (пустыми) данными
    const analysisDataRef = useRef(analysisData);
    analysisDataRef.current = analysisData;
    // Были ли в ячейках подписи «Динамики» или спарклайны в прошлый рендер таблицы (см. renderTable)
    const wasDecoratedRef = useRef(false);
    // Версия подписей/линий для строк: TableRow в BaseTable — PureComponent, и новый массив строк
    // с теми же объектами его не перерисовывает. Меняющийся data-атрибут (rowProps) перерисует
    const decorVersionRef = useRef(0);
    // «Поколение» запросов прошлого периода: ответы после перезагрузки или выключения отбрасываем
    const analysisGenerationRef = useRef(0);
    // Прошлый период считаем по значениям дат, а не по ссылке на объект: блоки дашборда
    // передают overridePeriod новым объектом на каждый рендер
    const periodStartValue = period?.start ? formatPeriodDate(period.start, true) : '';
    const periodEndValue = period?.end ? formatPeriodDate(period.end, true) : '';
    const periodInterval = (period as { interval?: string } | undefined)?.interval;
    const periodWithTime = period?.withTime;
    const analysisPeriod = useMemo(
        () => getPreviousPeriod(periodStartValue && periodEndValue
            ? { start: periodStartValue, end: periodEndValue, interval: periodInterval, withTime: periodWithTime }
            : null),
        [ periodStartValue, periodEndValue, periodInterval, periodWithTime ]
    );

    const resetAnalysis = useCallback(() => {
        analysisGenerationRef.current += 1;
        isAnalysisRef.current = false;
        setIsAnalysis(false);
        setAnalysisLoading(false);
        setAnalysisData(EMPTY_ANALYSIS_DATA);
    }, []);

    // Смена отчёта — выключаем: строки и метрики уже другие
    useEffect(() => {
        resetAnalysis();
    }, [ reportType, resetAnalysis ]);

    // Спарклайны: мини-график по периодам в ячейках колонок из sparklines (меню «три точки»).
    // Строки уровня — запрос /filter/table-series, «Итого» — /filter/chart (итог по тем же отрезкам)
    const globalDetails = useTypedSelector((state) => state.details.details);
    const effectiveDetails: string = (isDashboardBlock ? blockSettings.details : globalDetails) || 'd';
    const sparkKeys = useMemo(() => {
        const enabled = new Set<string>(Array.isArray(rawSparklines) ? rawSparklines : []);
        return metrics
            .map((metric) => metric?.dataKey as string | undefined)
            .filter((dataKey): dataKey is string => Boolean(dataKey) && enabled.has(dataKey as string));
    }, [ rawSparklines, metrics ]);
    const sparkKeysKey = sparkKeys.join(',');
    const sparkKeysRef = useRef<string[]>(sparkKeys);
    sparkKeysRef.current = sparkKeys;
    const isSparkActive = sparkKeys.length > 0 && !isCompare;
    const isSparkActiveRef = useRef(isSparkActive);
    isSparkActiveRef.current = isSparkActive;

    // Заливка ячеек обычных метрик по условиям столбца («…» → «Заливка ячеек»).
    // Ячейки читают из ref — по той же причине, что и «Динамика» (см. analysisDataRef)
    const cellFills = useMemo<Record<string, CellFillSettings>>(
        () => (rawCellFills && typeof rawCellFills === 'object' && !Array.isArray(rawCellFills)
            ? rawCellFills as Record<string, CellFillSettings>
            : EMPTY_CELL_FILLS),
        [ rawCellFills ],
    );
    const cellFillsRef = useRef(cellFills);
    cellFillsRef.current = cellFills;
    const hasCellFills = Object.keys(cellFills).length > 0;

    const [ sparkData, setSparkData ] = useState<SparkData>(EMPTY_SPARK_DATA);
    // Ячейки читают линии из ref — по той же причине, что и «Динамика» (см. analysisDataRef)
    const sparkDataRef = useRef(sparkData);
    sparkDataRef.current = sparkData;
    // Поколение запросов линий и уже запрошенные уровни ('' — верхний, иначе путь родителя)
    const [ sparkEpoch, setSparkEpoch ] = useState(0);
    const sparkGenerationRef = useRef(0);
    const sparkRequestedRef = useRef<Set<string>>(new Set());
    // После перезагрузки таблицы ждём новые строки: этот (старый) массив не запрашиваем
    const sparkStaleRowsRef = useRef<TableRowData[] | null>(null);
    // Очередь запросов линий (не больше SPARKLINE_MAX_PARALLEL одновременно)
    const sparkQueueRef = useRef<Array<() => Promise<void>>>([]);
    const sparkRunningRef = useRef(0);
    const pumpSparkQueue = useCallback(() => {
        while (sparkRunningRef.current < SPARKLINE_MAX_PARALLEL && sparkQueueRef.current.length > 0) {
            const task = sparkQueueRef.current.shift();
            if (!task) break;
            sparkRunningRef.current += 1;
            task().finally(() => {
                sparkRunningRef.current -= 1;
                pumpSparkQueue();
            });
        }
    }, []);

    // keepData — старые линии остаются на экране, пока не придут новые (добавили колонку: данные те же)
    const resetSparklines = useCallback((staleRows: TableRowData[] | null, keepData = false) => {
        sparkGenerationRef.current += 1;
        sparkRequestedRef.current = new Set();
        // Запросы старого поколения, которые ещё не ушли, не отправляем
        sparkQueueRef.current = [];
        sparkStaleRowsRef.current = staleRows;
        setSparkData((prev) => (keepData ? { ...prev, pending: {}, totalPending: false } : EMPTY_SPARK_DATA));
        setSparkEpoch((prev) => prev + 1);
    }, []);

    // Синхронизация Set с массивом loadRowKeys
    useEffect(() => {
        loadRowKeysSet.current = new Set(loadRowKeys);
    }, [loadRowKeys]);

    // Синхронизация Set с массивом expandedAll
    useEffect(() => {
        expandedAllSet.current = new Set(expandedAll);
    }, [expandedAll]);

    // Множество pathStr2 строк, у которых нужно скрыть иконку и отступ раскрытия
    // (сама родительская строка + все её потомки при «Раскрыть всё»)
    const descendantsOfExpandedAll = useMemo(() => {
        const set = new Set<string>();
        if (expandedAll.length === 0) return set;
        const collectDescendants = (children: TableRowData[] | undefined) => {
            if (!children) return;
            children.forEach((child) => {
                if (child.pathStr2) set.add(child.pathStr2);
                collectDescendants(child.children as TableRowData[] | undefined);
            });
        };
        tableData.data.forEach((row) => {
            if (expandedAllSet.current.has(row.pathStr2)) {
                set.add(row.pathStr2);
                collectDescendants(row.children as TableRowData[] | undefined);
            }
        });
        return set;
    }, [expandedAll, tableData.data]);

    // Обновление ref при смене типа отчёта и очистка при размонтировании
    useEffect(() => {
        currentReportTypeRef.current = reportType;
        // Отменяем предыдущие запросы при смене типа отчёта
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (compareAbortControllerRef.current) {
            compareAbortControllerRef.current.abort();
        }
        // Очищаем Sets при смене отчёта
        downloadedRowKeysSet.current.clear();
        loadRowKeysSet.current.clear();
        expandedAllSet.current.clear();
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (compareAbortControllerRef.current) {
                compareAbortControllerRef.current.abort();
            }
        };
    }, [reportType]);

    // Используем ref для action creators и текущих значений лимитов,
    // т.к. useActions() возвращает новые ссылки на каждом рендере
    const groupsActionsRef = useRef({ setMetricsLimit, setGroupsLimit });
    groupsActionsRef.current = { setMetricsLimit, setGroupsLimit };
    
    // Ref для текущих значений лимитов — чтобы сравнивать перед dispatch
    const currentLimitsRef = useRef({ metricsLimit, groupsLimit });
    currentLimitsRef.current = { metricsLimit, groupsLimit };

    const handleGroupsChanged = useCallback((
        _groupsChecked: { pathStr?: string }[],
        _metricsChecked: { name?: string; hideLine?: boolean }[],
        _totalChecked: { pathStr?: string }[],
        _chartType: string
    ) => {
        const currentLimits = currentLimitsRef.current;
        
        if (_chartType === 'bubble') {
            // Диспатчим только если значение изменилось
            if (currentLimits.metricsLimit !== false) {
                groupsActionsRef.current.setMetricsLimit(false);
            }
            if (currentLimits.groupsLimit !== false) {
                groupsActionsRef.current.setGroupsLimit(false);
            }
            return;
        }

        // Ограничение метрик: если выбраны группы, метрики ограничены
        const newMetricsLimit = _groupsChecked.length > 0;
        if (currentLimits.metricsLimit !== newMetricsLimit) {
            groupsActionsRef.current.setMetricsLimit(newMetricsLimit);
        }

        // Ограничение групп: если на графике больше одной видимой линии метрики.
        // ВАЖНО: на линейных графиках повторный клик по кнопке метрики не удаляет её из
        // metricsChecked, а лишь скрывает линию (hideLine). Считать такие метрики в лимите
        // нельзя — иначе ограничение «залипает»: линия на графике одна, а множественный
        // выбор строк группировок остаётся заблокированным.
        // Для типа "Таблица" ограничение отключено (используем ref чтобы избежать циклов)
        const visibleMetricsCount = _metricsChecked.filter((item) => item?.hideLine !== true).length;
        const newGroupsLimit = disableGroupsLimitRef.current ? false : visibleMetricsCount > 1;
        if (currentLimits.groupsLimit !== newGroupsLimit) {
            groupsActionsRef.current.setGroupsLimit(newGroupsLimit);
        }
    }, []);

    useEffect(() => {
        handleGroupsChanged(groupsChecked, metricsChecked, totalChecked, chartType);
    }, [groupsChecked, metricsChecked, totalChecked, chartType, handleGroupsChanged]);

    // Debounced функция для обновления ширины таблицы
    // В compact-режиме используем реальную ширину контейнера без минимума,
    // чтобы BaseTable корректно показывал горизонтальный скролл
    const minWidth = isCompactMode ? 0 : TABLE_MIN_WIDTH;
    const debouncedSetTableWidth = useDebounce((width: number) => {
        setTableWidth(width <= minWidth ? minWidth : width);
    }, DEBOUNCE_RESIZE_MS);

    useEffect(() => {
        const tableDiv = tableDivRef.current;
        if (tableDiv) {
            const calcTableWidth = () => {
                const width = tableDiv.offsetWidth;
                debouncedSetTableWidth(width);
            };

            // Проверка поддержки ResizeObserver для старых браузеров
            if (typeof ResizeObserver !== 'undefined') {
                const resizeObserver = new ResizeObserver(calcTableWidth);
                resizeObserver.observe(tableDiv);
                // Инициализация без debounce
                const initWidth = tableDiv.offsetWidth;
                setTableWidth(initWidth <= minWidth ? minWidth : initWidth);

                return () => {
                    resizeObserver.unobserve(tableDiv);
                    resizeObserver.disconnect();
                };
            } else {
                // Fallback для старых браузеров
                const initWidth = tableDiv.offsetWidth;
                setTableWidth(initWidth <= minWidth ? minWidth : initWidth);
            }
        }
    }, [debouncedSetTableWidth, minWidth]);

    const loadCompareRows = (loadCurrent?: TableRowData) => {
        // Отменяем предыдущий запрос сравнения если он не для дочерних данных
        if (!loadCurrent && compareAbortControllerRef.current) {
            compareAbortControllerRef.current.abort();
        }
        if (!loadCurrent) {
            compareAbortControllerRef.current = new AbortController();
        }

        // Показываем глобальный лоадер только при загрузке всей таблицы, не при раскрытии группировки
        if (!loadCurrent) {
            setMetaTableLoading({ value: true });
        }

        const { compareFilters1, compareFilters2 } = compareFilters;
        const { comparePeriod1, comparePeriod2 } = comparePeriod;
        const { attribution, type_applications } = attributes;

        const format = 'YYYY-MM-DD';
        const groupings_names = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));
        const metrics_names = metrics.map((col) => col?.dataKey).filter(Boolean);
        const expanded_ids = loadCurrent ? loadCurrent?.clientData?.path : [];

        const _comparePeriod1 = {
            start: moment(comparePeriod1.start).format(format),
            end: moment(comparePeriod1.end).format(format),
        };
        const _comparePeriod2 = {
            start: moment(comparePeriod2.start).format(format),
            end: moment(comparePeriod2.end).format(format),
        };

        const data: Record<string, any> = {
            comparePeriod1: _comparePeriod1,
            comparePeriod2: _comparePeriod2,
            compareFilters1: compareFilters1,
            compareFilters2: compareFilters2,
            attribution: {
                attribution: attribution,
                type_applications: type_applications,
            },
            groupings_names,
            metrics_names,
            expanded_ids,
            flat_mode: flatModeRef.current,
        };

        // Сохраняем текущий тип отчёта для проверки при получении ответа
        const requestReportType = reportType;
        
        unApi
            .getUniversalReportTableData(data, location.pathname)
            .then((response) => {
                // Проверяем, не был ли запрос отменён
                if (!loadCurrent && compareAbortControllerRef.current?.signal.aborted) {
                    return;
                }
                
                // Проверяем, что тип отчёта не изменился пока шёл запрос
                if (currentReportTypeRef.current !== requestReportType) {
                    return;
                }
                
                // Битый отчёт: ссылка на удалённую метрику — показываем причину и починку
                if (isDeletedMetricsError(response.data)) {
                    handleDeletedMetricsError(response.data, { metricsPool: metrics });
                    if (!loadCurrent) {
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }

                // Битый отчёт: ссылка на удалённую группу регистраций (код 255)
                if (isDeletedGroupsError(response.data)) {
                    handleDeletedGroupsError(response.data);
                    if (!loadCurrent) {
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }

                const data = response.data;

                if (data.success) {
                    if (loadCurrent) {
                        setTableChildrenData({ loadCurrent, data, groupings, blockId: dataBlockId, groupFilter });
                        setExpandedRowKeys((prev: string[]) => [...prev, loadCurrent.pathStr2]);
                        setLoadRowKeys((prev: string[]) =>
                            prev.filter((item: string) => item !== loadCurrent.pathStr2)
                        );
                    } else {
                        setTableData({ ...data, groupings, blockId: dataBlockId, flatMode: flatModeRef.current, groupFilter });
                        setExpandedRowKeys([]);
                        setLoadRowKeys([]);
                    }
                } else {
                    showErrorMessage('Ошибка при получении данных compareRows');
                    if (response.status === 504) {
                        showErrorMessage(
                            'Запрос выполняется слишком долго, из-за большого количества данных. Можно уменьшить период отчёта, удалить не нужные метрики или фильтры.'
                        );
                    }
                }
                // Сбрасываем глобальный лоадер только если он был установлен (при загрузке всей таблицы)
                if (!loadCurrent) {
                    setMetaTableLoading({ value: false, blockId });
                }
            })
            .catch((error) => {
                // Игнорируем ошибки отменённых запросов
                if (isAbortError(error) || (!loadCurrent && compareAbortControllerRef.current?.signal.aborted)) {
                    if (!compareAbortControllerRef.current || compareAbortControllerRef.current.signal.aborted) {
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }
                
                if (error?.response?.status !== 401) {
                    showErrorMessage('Ошибка при получении данных compareRows');
                }
                // Сбрасываем глобальный лоадер только если он был установлен (при загрузке всей таблицы)
                if (!loadCurrent) {
                    setMetaTableLoading({ value: false, blockId });
                }
            }).finally(() => {
                setAllBlocksFetching({ value: false });
                setMetaFilterUpdate({ value: false });
                setAllDataFetching({ value: false });
            });
    };

    const loadRows = (loadCurrent?: TableRowData) => {
        // Валидация данных перед запросом
        if (!validateTableRequest(groupings, metrics, period, 'ReportTable.loadRows')) {
            setMetaTableLoading({ value: false, blockId });
            return;
        }
        
        // Отменяем предыдущий запрос если он не для дочерних данных
        if (!loadCurrent && abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (!loadCurrent) {
            abortControllerRef.current = new AbortController();
        }

        safeLocalStorage.setItem('lastLoadDateTime', JSON.stringify({ time: new Date().toString(), completed: false }));
        let __attribution = attributes?.attribution;
        let __type_app = attributes?.type_applications;
        let __period = period;

        // Показываем глобальный лоадер только при загрузке всей таблицы, не при раскрытии группировки
        if (!loadCurrent) {
            setMetaTableLoading({ value: true, blockId });
        }

        // Обновление даты пересбора связи групп (membership_synced_at): демон на бэке
        // пишет свежую дату в БД, но список групп кэшируется и застывает. При полной
        // загрузке/обновлении отчёта перезапрашиваем список с refetch, если в оси есть
        // группа регистраций — так дата в строках-группах становится актуальной.
        // Выбор группировок (state.table.groupings) не трогаем — обновляем только данные списка.
        if (
            !loadCurrent &&
            selectedSchool?.id &&
            groupings.some((col: any) => typeof col?.dataKey === 'string' && col.dataKey.startsWith('custom_group'))
        ) {
            getCustomGroups(selectedSchool.id, location.pathname, undefined, true);
        }

        const _period = {
            start: formatPeriodDate(__period.start, __period.withTime),
            end: formatPeriodDate(__period.end, __period.withTime),
        };
        const groupings_names = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));
        const metrics_names = metrics.map((col) => col?.dataKey).filter(Boolean);
        const expanded_ids = loadCurrent?.clientData?.path ?? [];

        if (expanded_ids.length === groupings_names.length) {
            if (!loadCurrent) {
                setMetaTableLoading({ value: false, blockId });
            }
            safeLocalStorage.setItem('lastLoadDateTime', JSON.stringify({ time: new Date().toString(), completed: true }));
            return;
        }

        let data: Record<string, any> = {
            period: _period,
            filters: mergeAndBuildApiFilters({
                blockFilters: filters,
                globalDashboardFilters: commonFilters,
                isDashboardBlock: isDashboardBlock,
            }),
            attribution: {
                attribution:
                    typeof __attribution === 'string' ? [__attribution] : __attribution,
                type_applications:
                    typeof __type_app === 'string' ? [__type_app] : __type_app,
            },
            groupings_names,
            metrics_names,
            expanded_ids,
            flat_mode: flatModeRef.current,
        };

        safeLocalStorage.setItem('prevRequestState', JSON.stringify(data));

        // Сохраняем текущий тип отчёта для проверки при получении ответа
        const requestReportType = reportType;
        // Окно блока: заставка этой таблицы до ответа на её собственный запрос
        const tracksOwnLoading = isEditingModal && !loadCurrent;
        const ownLoadSeq = tracksOwnLoading ? ++ownLoadSeqRef.current : 0;
        if (tracksOwnLoading) setOwnTableLoading(true);

        unApi
            .getUniversalReportTableData(data, location.pathname)
            .then((response) => {
                // Проверяем, не был ли запрос отменён или тип отчёта изменился
                if (!loadCurrent && abortControllerRef.current?.signal.aborted) {
                    return;
                }
                
                // Проверяем, что тип отчёта не изменился пока шёл запрос
                if (currentReportTypeRef.current !== requestReportType) {
                    return;
                }

                // Битый отчёт: ссылка на удалённую метрику — до обращения к rows
                if (isDeletedMetricsError(response.data)) {
                    handleDeletedMetricsError(response.data, { metricsPool: metrics });
                    if (!loadCurrent) {
                        setMetaTableFetching({ value: false, blockId });
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }

                // Битый отчёт: ссылка на удалённую группу регистраций (код 255)
                if (isDeletedGroupsError(response.data)) {
                    handleDeletedGroupsError(response.data);
                    if (!loadCurrent) {
                        setMetaTableFetching({ value: false, blockId });
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }

                const rowsWithIdAsString = response.data.rows.map((row: TableRowData) => {
                    if (row.id) {
                        return { ...row, id: String(row.id) };
                    }
                    return row;
                });

                const data = { ...response.data, rows: rowsWithIdAsString };

                if (data.success) {
                    if (loadCurrent) {
                        setTableChildrenData({ loadCurrent, data, groupings, blockId: dataBlockId, groupFilter });
                        setExpandedRowKeys((prev: string[]) => [...prev, loadCurrent.pathStr2]);
                        setLoadRowKeys((prev: string[]) =>
                            prev.filter((item: string) => item !== loadCurrent.pathStr2)
                        );
                        // Триггерим обновление UI после получения дочерних данных
                        triggerUpdate();
                    } else {
                        setTableData({ ...data, groupings, blockId: dataBlockId, flatMode: flatModeRef.current, groupFilter });
                        setExpandedRowKeys([]);
                        setLoadRowKeys([]);
                        // Триггерим обновление UI после получения данных
                        triggerUpdate();
                    }
                } else {
                    showErrorMessage('Ошибка при получении данных');
                    if (response.status === 504) {
                        showErrorMessage(
                            'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
                        );
                    }
                    if (!loadCurrent) {
                        setMetaTableFetching({ value: false, blockId });
                    }
                }
                // Сбрасываем глобальный лоадер только если он был установлен (при загрузке всей таблицы)
                if (!loadCurrent) {
                    setMetaTableLoading({ value: false, blockId });
                }
            })
            .catch((error) => {
                // Игнорируем ошибки отменённых запросов, но сбрасываем лоадер
                if (isAbortError(error) || (!loadCurrent && abortControllerRef.current?.signal.aborted)) {
                    // Сбрасываем лоадер только если нет нового активного запроса
                    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
                        setMetaTableLoading({ value: false, blockId });
                    }
                    return;
                }

                if (error?.response?.status !== 401) {
                    showErrorMessage('Ошибка при получении данных');
                }
                if (error.response) {
                    if (error.response.status === 504) {
                        showErrorMessage(
                            'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
                        );
                    }
                }

                dataRef.current.data = [];
                // Сбрасываем глобальный лоадер только если он был установлен (при загрузке всей таблицы)
                if (!loadCurrent) {
                    setMetaTableLoading({ value: false, blockId });
                }
            }).finally(() => {
                // Ответ устаревшего (перезапрошенного) запроса заставку не снимает — ждём последний
                if (tracksOwnLoading && isMountedRef.current && ownLoadSeq === ownLoadSeqRef.current) {
                    setOwnTableLoading(false);
                }
                safeLocalStorage.setItem('lastLoadDateTime', JSON.stringify({ time: new Date().toString(), completed: true }));
                setAllBlocksFetching({ value: false });
                setMetaFilterUpdate({ value: false });
                setAllDataFetching({ value: false });
            });
    };

    // «Динамика»: те же строки (фильтры, группировки, атрибуция, метрики) за прошлый период.
    // Без loadCurrent — верхний уровень и «Итого», с loadCurrent — дети раскрытой строки.
    const loadAnalysisRows = (loadCurrent?: TableRowData) => {
        if (!analysisPeriod || isCompare) return;
        if (!validateTableRequest(groupings, metrics, period, 'ReportTable.loadAnalysisRows')) return;

        const groupings_names = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));
        const parentPath: string[] = loadCurrent?.clientData?.path ?? [];
        if (parentPath.length >= groupings_names.length) return;

        const isFlat = flatModeRef.current;
        const parentKey = isFlat ? '' : parentPath.join('-');
        const periodLabel = formatAnalysisPeriod(analysisPeriod, period.withTime);

        if (!loadCurrent) {
            analysisGenerationRef.current += 1;
            setAnalysisData({ ...EMPTY_ANALYSIS_DATA, periodLabel });
            setAnalysisLoading(true);
        }
        const generation = analysisGenerationRef.current;
        const requestReportType = reportType;

        const data: Record<string, any> = {
            period: {
                start: formatPeriodDate(analysisPeriod.start, period.withTime),
                end: formatPeriodDate(analysisPeriod.end, period.withTime),
            },
            filters: mergeAndBuildApiFilters({
                blockFilters: filters,
                globalDashboardFilters: commonFilters,
                isDashboardBlock: isDashboardBlock,
            }),
            attribution: {
                attribution:
                    typeof attributes?.attribution === 'string' ? [ attributes.attribution ] : attributes?.attribution,
                type_applications:
                    typeof attributes?.type_applications === 'string' ? [ attributes.type_applications ] : attributes?.type_applications,
            },
            groupings_names,
            metrics_names: metrics.map((col) => col?.dataKey).filter(Boolean),
            expanded_ids: parentPath,
            flat_mode: isFlat,
        };

        unApi
            .getUniversalReportTableData(data, location.pathname)
            .then((response) => {
                if (generation !== analysisGenerationRef.current || currentReportTypeRef.current !== requestReportType) {
                    return;
                }
                const resData = response.data;
                // Битый отчёт (удалённая метрика/группа) объясняет основная загрузка — здесь молчим
                if (isDeletedMetricsError(resData) || isDeletedGroupsError(resData)) return;
                if (!resData?.success) {
                    if (!loadCurrent) showErrorMessage('Не удалось загрузить данные за прошлый период');
                    return;
                }
                const rows: Record<string, TableRowData> = {};
                (Array.isArray(resData.rows) ? resData.rows : []).forEach((row: TableRowData) => {
                    // id как в loadRows: строкой, иначе путь не совпадёт с путём строки таблицы
                    const id = row.id ? String(row.id) : row.id;
                    rows[buildAnalysisRowKey([ ...parentPath, id ], row.group, isFlat)] = row;
                });
                setAnalysisData((prev) => ({
                    ...prev,
                    rows: { ...prev.rows, ...rows },
                    loadedParents: { ...prev.loadedParents, [parentKey]: true },
                    total: loadCurrent ? prev.total : (resData.total ?? null),
                }));
            })
            .catch((error) => {
                if (generation !== analysisGenerationRef.current || isAbortError(error)) return;
                if (error?.response?.status !== 401) {
                    showErrorMessage('Не удалось загрузить данные за прошлый период');
                }
            })
            .finally(() => {
                if (!loadCurrent && generation === analysisGenerationRef.current) {
                    setAnalysisLoading(false);
                }
            });
    };
    // Раскрытие строк вызывается из useCallback с устаревшими замыканиями — берём свежую функцию
    const loadAnalysisRowsRef = useRef(loadAnalysisRows);
    loadAnalysisRowsRef.current = loadAnalysisRows;

    const handleAnalysisToggle = () => {
        if (isAnalysis) {
            resetAnalysis();
            return;
        }
        isAnalysisRef.current = true;
        setIsAnalysis(true);
        loadAnalysisRows();
        // Строки, у которых дети уже загружены (раскрыты сейчас или раскрывались и свёрнуты):
        // повторное раскрытие детей не перезапрашивает, поэтому их прошлый период берём сразу
        const walk = (rows: TableRowData[] | null | undefined) => {
            (rows ?? []).forEach((row) => {
                if (row.clientData?.downloaded) {
                    loadAnalysisRows(row);
                }
                if (Array.isArray(row.children)) walk(row.children);
            });
        };
        walk(tableData.data);
    };
    const handleAnalysisToggleRef = useRef(handleAnalysisToggle);
    handleAnalysisToggleRef.current = handleAnalysisToggle;

    // Кнопка «Динамика»: в блоке дашборда и его окне ещё и запоминаем выбор
    const handleAnalysisButton = () => {
        const next = !isAnalysisRef.current;
        handleAnalysisToggleRef.current();
        if (storesTableToggles) {
            saveTableToggle(setTableAnalysis, next);
        }
    };

    // В «Сравнить» и для «За всё время» «Динамика» не работает — выключаем
    useEffect(() => {
        if ((isCompare || !analysisPeriod) && isAnalysisRef.current) {
            resetAnalysis();
        }
    }, [ isCompare, analysisPeriod, resetAnalysis ]);

    // Блок дашборда / окно блока: включаем «Динамику», сохранённую в блоке (и выключаем, если убрали).
    // Стоит после сброса при смене отчёта — иначе тот сразу бы её выключил.
    // Данные прошлого периода догрузит основная загрузка таблицы (см. isAnalysisRef в эффекте загрузки)
    const analysisSyncReportTypeRef = useRef(reportType);
    useEffect(() => {
        // Сменился тип отчёта — в tableData ещё строки старого: прошлый период запросит новая основная загрузка
        const reportTypeChanged = analysisSyncReportTypeRef.current !== reportType;
        analysisSyncReportTypeRef.current = reportType;
        if (!storesTableToggles) return;
        const wanted = rawAnalysis === true && !isCompare && Boolean(analysisPeriod);
        if (wanted && !isAnalysisRef.current) {
            isAnalysisRef.current = true;
            setIsAnalysis(true);
            // Данные уже загружены и новая загрузка не идёт (иначе прошлый период запросит она сама)
            const loadingNow = (isEditingModal ? ownTableLoading : isTableLoading) || effectiveTableFetching;
            if (!reportTypeChanged && tableData.data.length > 0 && !loadingNow) {
                loadAnalysisRowsRef.current();
            }
        } else if (!wanted && rawAnalysis !== true && isAnalysisRef.current) {
            resetAnalysis();
        }
    }, [ storesTableToggles, rawAnalysis, isCompare, analysisPeriod, reportType, resetAnalysis ]);

    // Спарклайны: общая часть запроса — как у loadRows (период, фильтры, атрибуция, группировки)
    const buildSparkBasePayload = () => ({
        period: {
            start: formatPeriodDate(period.start, period.withTime),
            end: formatPeriodDate(period.end, period.withTime),
        },
        filters: mergeAndBuildApiFilters({
            blockFilters: filters,
            globalDashboardFilters: commonFilters,
            isDashboardBlock: isDashboardBlock,
        }),
        attribution: {
            attribution:
                typeof attributes?.attribution === 'string' ? [ attributes.attribution ] : attributes?.attribution,
            type_applications:
                typeof attributes?.type_applications === 'string' ? [ attributes.type_applications ] : attributes?.type_applications,
        },
        groupings_names: buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType)),
    });

    // Спарклайны: запрос закончился (любым исходом) — снимаем заглушку «грузится» со строк и, если задано, с «Итого»
    const settleSpark = (generation: number, rowKeys: string[], totalPending?: boolean) => {
        if (generation !== sparkGenerationRef.current) return;
        setSparkData((prev) => ({
            ...prev,
            pending: withoutKeys(prev.pending, rowKeys),
            totalPending: totalPending ?? prev.totalPending,
        }));
    };

    // Спарклайны: линия «Итого» — итог графика по тем же отрезкам, что пришли для строк
    const loadSparkTotal = (details: string, generation: number) => {
        const keys = sparkKeysRef.current;
        const payload = {
            ...buildSparkBasePayload(),
            details,
            selected_groups_path: [ [ 'total' ] ],
            selected_metrics_names: keys,
        };
        // «Итого» — в начало очереди: иначе на длинной таблице ждал бы все порции строк
        sparkQueueRef.current.unshift(() => unApi
            .getUniversalReportChartData(payload, location.pathname)
            .then((response) => {
                if (generation !== sparkGenerationRef.current) return;
                const total = response.data?.data?.total ?? response.data?.total;
                if (!total || typeof total !== 'object') {
                    settleSpark(generation, [], false);
                    return;
                }
                const series: Record<string, Array<number | null>> = {};
                keys.forEach((key) => {
                    const points = total[key];
                    if (Array.isArray(points)) {
                        series[key] = points.map((point: { y?: unknown }) => parseMetricValue(point?.y));
                    }
                });
                setSparkData((prev) => ({ ...prev, total: series, totalPending: false }));
            })
            .catch((error) => {
                if (generation !== sparkGenerationRef.current) return;
                settleSpark(generation, [], false);
                if (isAbortError(error)) return;
                // Линии — дополнение к таблице: без всплывающей ошибки, чтобы не мешать работе с отчётом
                console.error('Спарклайны: не удалось загрузить итог', error);
            }));
        pumpSparkQueue();
    };

    // Спарклайны уровня: parent — раскрытая строка (null — верхний уровень), rows — строки этого уровня
    const loadSparkLevel = (parent: TableRowData | null, rows: TableRowData[]) => {
        const parentPath: string[] = parent?.clientData?.path ?? [];
        // Порядок как на экране (сортировка и фильтры таблицы): лимит берёт верхние видимые строки
        const shownOrder = new Map<string, number>();
        const walkShown = (shown: TableRowData[] | null | undefined) => {
            (shown ?? []).forEach((row) => {
                if (row.pathStr) shownOrder.set(row.pathStr, shownOrder.size);
                walkShown(row.children as TableRowData[] | null | undefined);
            });
        };
        walkShown(dataRef.current.data);
        const orderOf = (row: TableRowData) => shownOrder.get(row.pathStr) ?? Number.MAX_SAFE_INTEGER;
        const levelRows = rows
            .filter((row) => row.group !== EMPTY_GROUP && row.id !== undefined && row.id !== null && row.id !== '')
            .sort((a, b) => orderOf(a) - orderOf(b))
            .slice(0, SPARKLINE_MAX_LEVEL_ROWS);
        if (levelRows.length === 0) return;
        const generation = sparkGenerationRef.current;
        const requestReportType = reportType;

        const rowKeyOf = (row: TableRowData) => buildAnalysisRowKey([ ...parentPath, String(row.id) ], null, false);
        // Пока линии в пути — в ячейках заглушка «грузится»: пустая ячейка выглядела бы поломкой
        const pendingAll: Record<string, true> = {};
        levelRows.forEach((row) => { pendingAll[rowKeyOf(row)] = true; });
        setSparkData((prev) => ({
            ...prev,
            pending: { ...prev.pending, ...pendingAll },
            totalPending: parent ? prev.totalPending : true,
        }));

        // Колонки — группами по SPARKLINE_METRICS_PER_REQUEST (столько сервер считает за раз),
        // строки — порциями по SPARKLINE_MAX_ROWS в порядке экрана: сначала линии у верхних строк,
        // остальное догружается очередью
        const allKeys = sparkKeysRef.current;
        const metricGroups: string[][] = [];
        for (let i = 0; i < allKeys.length; i += SPARKLINE_METRICS_PER_REQUEST) {
            metricGroups.push(allKeys.slice(i, i + SPARKLINE_METRICS_PER_REQUEST));
        }

        for (let start = 0; start < levelRows.length; start += SPARKLINE_MAX_ROWS) {
            const isFirstChunk = start === 0;
            const chunk = levelRows.slice(start, start + SPARKLINE_MAX_ROWS);
            const chunkKeys = chunk.map(rowKeyOf);
            // Заглушку «грузится» у строк порции снимаем, когда пришли все группы колонок
            let groupsLeft = metricGroups.length;
            const settleGroup = (settleTotal?: boolean) => {
                groupsLeft -= 1;
                settleSpark(generation, groupsLeft === 0 ? chunkKeys : [], settleTotal);
            };

            metricGroups.forEach((metricGroup, groupIndex) => {
                // Первая порция и первая группа колонок верхнего уровня решают судьбу «Итого»:
                // без их details итог не запросить
                const isTotalLeader = !parent && isFirstChunk && groupIndex === 0;
                const settleTotal = isTotalLeader ? false : undefined;
                const payload = {
                    ...buildSparkBasePayload(),
                    details: effectiveDetails,
                    metrics_names: metricGroup,
                    expanded_ids: parentPath,
                    ids: chunk.map((row) => String(row.id)),
                };

                sparkQueueRef.current.push(() => unApi
                    .getUniversalReportTableSeries(payload, location.pathname)
                    .then((response) => {
                        if (generation !== sparkGenerationRef.current) return;
                        if (currentReportTypeRef.current !== requestReportType) {
                            settleGroup(settleTotal);
                            return;
                        }
                        const resData = response.data;
                        // Отчёт или уровень без динамики по датам — говорим один раз (верхний уровень), чтобы кнопка не «молчала»
                        if (!parent && (resData?.reason === 'not_supported' || resData?.reason === 'date_level')) {
                            settleGroup(settleTotal);
                            if (!isTotalLeader) return;
                            message.info(resData.reason === 'date_level'
                                ? 'Спарклайн не строится для группировки по датам'
                                : 'Спарклайн недоступен для этого отчёта');
                            return;
                        }
                        if (!resData?.success || !resData.rows || typeof resData.rows !== 'object') {
                            settleGroup(settleTotal);
                            return;
                        }
                        const byKey: SparkData['rows'] = {};
                        Object.entries(resData.rows as Record<string, Record<string, Array<number | null>>>).forEach(([ id, series ]) => {
                            byKey[buildAnalysisRowKey([ ...parentPath, id ], null, false)] = series;
                        });
                        const labels: string[] = Array.isArray(resData.labels) ? resData.labels : [];
                        const details: string | null = typeof resData.details === 'string' ? resData.details : null;
                        const startTotal = isTotalLeader && Boolean(details);
                        groupsLeft -= 1;
                        const chunkDone = groupsLeft === 0;
                        setSparkData((prev) => {
                            // Линии группы дописываем к уже пришедшим линиям строки, а не заменяем их
                            const rowsNext = { ...prev.rows };
                            Object.entries(byKey).forEach(([ key, series ]) => {
                                rowsNext[key] = { ...(prev.rows[key] ?? {}), ...series };
                            });
                            return {
                                ...prev,
                                rows: rowsNext,
                                labels: labels.length > 0 ? labels : prev.labels,
                                details: details ?? prev.details,
                                pending: chunkDone ? withoutKeys(prev.pending, chunkKeys) : prev.pending,
                                // «Итого» ждёт своего запроса; без details его не будет — снимаем заглушку
                                totalPending: settleTotal === undefined || startTotal ? prev.totalPending : false,
                            };
                        });
                        if (startTotal && details) {
                            loadSparkTotal(details, generation);
                        }
                    })
                    .catch((error) => {
                        if (generation !== sparkGenerationRef.current) return;
                        settleGroup(settleTotal);
                        if (isAbortError(error)) return;
                        console.error('Спарклайны: не удалось загрузить данные', error);
                    }));
            });
        }
        pumpSparkQueue();
    };
    const loadSparkLevelRef = useRef(loadSparkLevel);
    loadSparkLevelRef.current = loadSparkLevel;

    // Прошлый набор колонок и детализация: чтобы при добавлении/выключении колонки не стирать уже нарисованные линии
    const sparkPrevKeysRef = useRef<string[]>([]);
    const sparkPrevDetailsRef = useRef(effectiveDetails);

    // Включили/выключили спарклайн у колонки, сменили детализацию — линии заново по текущим строкам
    useEffect(() => {
        const prevKeys = sparkPrevKeysRef.current;
        const sameDetails = sparkPrevDetailsRef.current === effectiveDetails;
        const keys = sparkKeysRef.current;
        sparkPrevKeysRef.current = isSparkActive ? keys : [];
        sparkPrevDetailsRef.current = effectiveDetails;
        // Выключено и линий нет — сбрасывать нечего (не перерисовываем таблицу зря)
        if (!isSparkActive && sparkDataRef.current === EMPTY_SPARK_DATA && sparkRequestedRef.current.size === 0) return;
        // Набор колонок со спарклайном изменился — ширину считаем заново: выключенная колонка сужается обратно
        setAnalysisExtraWidth((prev) => (Object.keys(prev).length ? {} : prev));
        // Колонку только выключили — у остальных линии уже есть, перезапрашивать нечего
        if (isSparkActive && sameDetails && keys.every((key) => prevKeys.includes(key))) return;
        // Колонку добавили — старые линии остаются на месте, пока догружаются новые
        resetSparklines(null, isSparkActive && sameDetails && prevKeys.length > 0);
    }, [ sparkKeysKey, effectiveDetails, isSparkActive, resetSparklines ]);

    // Запрашиваем уровни, чьи строки уже есть в таблице: верхний, раскрытые, «Раскрыть всё»
    useEffect(() => {
        // Плоский режим бэк не поддерживает: в строках нет пути группировок
        if (!isSparkActive || flatModeRef.current) return;
        // Таблица перезагружается — старые строки не запрашиваем, ждём новые.
        // Загрузка закончилась, а строки те же (ошибка, битый отчёт) — берём текущие, иначе линии не появятся
        if (sparkStaleRowsRef.current && sparkStaleRowsRef.current === tableData.data
            && (isTableLoading || effectiveTableFetching)) return;
        sparkStaleRowsRef.current = null;

        const requested = sparkRequestedRef.current;
        if (!requested.has('') && tableData.data.length > 0) {
            requested.add('');
            loadSparkLevelRef.current(null, tableData.data);
        }
        const walk = (rows: TableRowData[] | null | undefined) => {
            (rows ?? []).forEach((row) => {
                const children = row.children as TableRowData[] | null | undefined;
                if (!row.clientData?.downloaded || !Array.isArray(children) || children.length === 0) return;
                const key = (row.clientData.path ?? []).join('-');
                if (!requested.has(key)) {
                    requested.add(key);
                    loadSparkLevelRef.current(row, children);
                }
                walk(children);
            });
        };
        walk(tableData.data);
    }, [ isSparkActive, tableData.data, sparkEpoch, isTableLoading, effectiveTableFetching ]);

    useEffect(() => {
        safeLocalStorage.setItem('prevRequestState', JSON.stringify({}));
    }, []);

    useEffect(() => {
        if (selectedSchool?.id) {
            getCreatedMetrics(selectedSchool?.id);
        }
    }, [selectedSchool?.id, location.pathname]);

    // Подгружаем «Группы регистраций», чтобы у строк-групп в таблице показывался
    // плюсик «добавить в фильтр» (GroupCell читает state.table.customGroups).
    // Нужно, когда панель группировок скрыта и ReportGroupings не монтируется —
    // прежде всего виджеты дашборда/монитор. getCustomGroups НЕ в deps: useActions
    // отдаёт новую ссылку каждый рендер, её включение зациклило бы эффект.
    useEffect(() => {
        const hasGroupsNode = reportType === 'universal' || reportType === 'ad'
            || reportType === 'order' || reportType === 'payment' || reportType === 'webinar';
        if (hasGroupsNode && selectedSchool?.id) {
            getCustomGroups(selectedSchool.id, location.pathname, undefined, false);
        }
        // «Параметры Яндекс.Директ» — только в отчёте рекламы: нужны GroupCell для плюсика
        // «добавить в фильтр» на строках осей utm_param_*/utm_cabinet_* (дерево фильтров достраивается из них)
        if (reportType === 'ad' && selectedSchool?.id) {
            getUtmSections(selectedSchool.id, undefined, false);
        }
    }, [selectedSchool?.id, reportType]);

    // Создаём маппинг кастомных метрик для расчёта итого и среднего
    const customMetricsMap = useMemo<CustomMetricsMap>(() => {
        const map: CustomMetricsMap = {};
        metrics.forEach((metric) => {
            if (metric?.dataKey?.startsWith('_')) {
                map[metric.dataKey] = {
                    base_metric_name: metric.base_metric_name,
                    metric_formula: metric.metric_formula,
                    unit: metric.unit
                };
            }
        });
        return map;
    }, [metrics]);

    // Синхронное вычисление totalChosenMetrics через useMemo для мгновенного обновления UI
    const computedTotalChosenMetrics = useMemo(() => {
        if (groupsChecked.length === 0) {
            const initialChosenMetrics: Record<string, number> = {};
            metrics.forEach((el) => {
                if (el?.dataKey) {
                    initialChosenMetrics[el.dataKey] = 0;
                }
            });
            return initialChosenMetrics;
        } else {
            // Вычисляем сумму/среднее для выбранных строк
            const result = getSummaryOfGroupings(groupsChecked, tableData.data, customMetricsMap);
            return result || {};
        }
    }, [groupsChecked, tableData.data, customMetricsMap, metrics]);

    // «Итого и среднее выбранных» за прошлый период: те же строки и та же формула суммы/среднего
    const analysisChosenMetrics = useMemo<Record<string, unknown> | null>(() => {
        if (!isAnalysis || groupsChecked.length === 0) return null;
        const prevRows: TableRowData[] = [];
        const walk = (rows: TableRowData[] | null | undefined) => {
            (rows ?? []).forEach((row) => {
                if (row.group !== EMPTY_GROUP && row.clientData?.path) {
                    const prevRow = analysisData.rows[buildAnalysisRowKey(row.clientData.path, row.group, flatMode)];
                    if (prevRow) {
                        // Путь и pathStr — от текущей строки: по ним getSummaryOfGroupings находит выбранные
                        prevRows.push({
                            ...prevRow,
                            id: row.id,
                            group: row.group,
                            pathStr: row.pathStr,
                            pathStr2: row.pathStr2,
                            clientData: row.clientData,
                            children: undefined,
                        });
                    }
                }
                if (Array.isArray(row.children)) walk(row.children);
            });
        };
        walk(tableData.data);
        return getSummaryOfGroupings(groupsChecked, prevRows, customMetricsMap) || {};
    }, [ isAnalysis, groupsChecked, tableData.data, analysisData.rows, flatMode, customMetricsMap ]);
    const analysisChosenRef = useRef(analysisChosenMetrics);
    analysisChosenRef.current = analysisChosenMetrics;

    // Спарклайн «Итого и среднее выбранных»: в каждой точке — та же формула, что у числа в этой строке
    // (getSummaryOfGroupings: сумма, для средних/конверсий/цен — среднее) по линиям отмеченных строк.
    // Если у какой-то отмеченной строки линии нет (не загрузилась) — для этой метрики не рисуем
    const sparkChosenSeries = useMemo<Record<string, Array<number | null>> | null>(() => {
        if (!isSparkActive || groupsChecked.length === 0 || sparkData.labels.length === 0) return null;
        const count = sparkData.labels.length;
        const checkedPaths = new Set<string>(groupsChecked.map((el: { pathStr?: string }) => el?.pathStr).filter(Boolean) as string[]);
        const selected: Array<{ row: TableRowData; series: Record<string, Array<number | null>> | undefined }> = [];
        const walk = (rows: TableRowData[] | null | undefined) => {
            (rows ?? []).forEach((row) => {
                if (row.group !== EMPTY_GROUP && row.clientData?.path && checkedPaths.has(row.pathStr)) {
                    selected.push({ row, series: sparkData.rows[buildAnalysisRowKey(row.clientData.path, null, false)] });
                }
                if (Array.isArray(row.children)) walk(row.children);
            });
        };
        walk(tableData.data);
        if (selected.length === 0) return null;

        const complete = sparkKeys.filter((key) => selected.every(({ series }) => Array.isArray(series?.[key])));
        if (complete.length === 0) return null;

        const result: Record<string, Array<number | null>> = {};
        complete.forEach((key) => { result[key] = []; });
        for (let index = 0; index < count; index++) {
            const emptyPoint = new Set<string>();
            const bucketRows = selected.map(({ row, series }) => {
                const bucketRow: TableRowData = {
                    id: row.id,
                    group: row.group,
                    pathStr: row.pathStr,
                    pathStr2: row.pathStr2,
                    clientData: row.clientData,
                };
                complete.forEach((key) => {
                    const value = series?.[key]?.[index];
                    if (value === null || value === undefined) emptyPoint.add(key);
                    bucketRow[key] = value ?? 0;
                });
                return bucketRow;
            });
            const summary = getSummaryOfGroupings(groupsChecked, bucketRows, customMetricsMap) || {};
            complete.forEach((key) => {
                result[key].push(emptyPoint.has(key) ? null : parseMetricValue(summary[key]));
            });
        }
        return result;
    }, [ isSparkActive, groupsChecked, tableData.data, sparkData, sparkKeys, customMetricsMap ]);
    const sparkChosenRef = useRef(sparkChosenSeries);
    sparkChosenRef.current = sparkChosenSeries;

    // Синхронизируем с Redux для других компонентов (но UI использует computedTotalChosenMetrics напрямую)
    useEffect(() => {
        // Предпросмотр со своими строками (dataSlotId) не пишет общие отметки окна: он считает по своим строкам
        if (dataSlotId) return;
        if (groupsChecked.length === 0) {
            setTotalChosenStatus([]);
        }
        setTotalChosenMetrics(computedTotalChosenMetrics);
    }, [computedTotalChosenMetrics, groupsChecked.length]);

    // Ref для предотвращения повторного применения режима выбора при каждом изменении tableData
    const lastAppliedSelectionRef = useRef<{ mode: string; n: number | null; dataLength: number }>({ mode: '', n: null, dataLength: 0 });

    // Применяем режимы 'all' или 'firstN' после загрузки данных таблицы
    useEffect(() => {
        // В flatMode автовыбор не применяем — данные плоские, выбор ручной.
        // Предпросмотр со своими строками (dataSlotId) общие галочки окна не выставляет
        if (flatMode || dataSlotId) {
            return;
        }

        // Проверяем, что данные загружены и есть группировки
        if (tableData.data.length === 0 || groupings.length === 0) {
            return;
        }

        // Для режимов 'all' и 'firstN' применяем выбор при каждом изменении данных
        // Это важно, потому что данные могут меняться со временем
        const shouldApply = 
            (groupsSelectionMode === 'all' || groupsSelectionMode === 'firstN') &&
            (lastAppliedSelectionRef.current.mode !== groupsSelectionMode ||
             lastAppliedSelectionRef.current.n !== selectFirstNRows ||
             lastAppliedSelectionRef.current.dataLength !== tableData.data.length);

        if (!shouldApply) {
            return;
        }

        // Обновляем ref
        lastAppliedSelectionRef.current = { 
            mode: groupsSelectionMode, 
            n: selectFirstNRows, 
            dataLength: tableData.data.length 
        };

        // Применяем режим выбора
        if (groupsSelectionMode === 'all') {
            // Получаем отфильтрованные данные
            const filteredData = finishRows({
                rows: tableData.data,
                filters: filtersTable,
                sort: sortTable,
                percentages,
                totalRow: tableData.total,
            });

            // Исключаем строки "Итого и среднее"
            const groupingRowsOnly = filteredData.filter((item: TableRowData) => 
                item.pathStr !== 'total' && item.pathStr !== 'total_choosen'
            );

            const allGroupings = groupingRowsOnly.map((item: TableRowData) => ({
                ids: [item.id ? item.id : item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));

            if (allGroupings.length > 0) {
                // Для блоков дашборда записываем в per-block state
                if (isDashboardBlock && blockId) {
                    setGroupingsChecked({ data: allGroupings, blockId });
                } else {
                    setGroupingsChecked(allGroupings);
                }
            }
        } else if (groupsSelectionMode === 'firstN' && selectFirstNRows && selectFirstNRows > 0) {
            // Получаем отфильтрованные данные
            const filteredData = finishRows({
                rows: tableData.data,
                filters: filtersTable,
                sort: sortTable,
                percentages,
                totalRow: tableData.total,
            });

            // Исключаем строки "Итого и среднее"
            const groupingRowsOnly = filteredData.filter((item: TableRowData) =>
                item.pathStr !== 'total' && item.pathStr !== 'total_choosen'
            );

            const firstNGroupings = groupingRowsOnly.slice(0, selectFirstNRows).map((item: TableRowData) => ({
                ids: [item.id ? item.id : item.pathStr || ''],
                pathStr: item.pathStr || '',
                label: item.group
            }));

            if (firstNGroupings.length > 0) {
                // Для блоков дашборда записываем в per-block state
                if (isDashboardBlock && blockId) {
                    setGroupingsChecked({ data: firstNGroupings, blockId });
                } else {
                    setGroupingsChecked(firstNGroupings);
                }
            }
        }
    }, [tableData.data.length, groupsSelectionMode, selectFirstNRows, groupings.length, isDashboardBlock, blockId, flatMode]);

    useEffect(() => {
        devLog('[ReportTable] useEffect:', { isNotParamsFilterVisible: isNotParamsFilterVisibleRef.current, effectiveTableFetching, isTableCompareFetching, isCompare, didUpdate, skipTableFetchUntil, now: Date.now() });
        // Блокируем загрузку данных при открытом модальном окне редактирования метрики
        // Используем ref, чтобы изменение флага не триггерило перезагрузку
        if (isNotParamsFilterVisibleRef.current) {
            devLog('[ReportTable] BLOCKED by isNotParamsFilterVisible');
            return;
        }
        
        // Блокируем загрузку если skipTableFetchUntil ещё не истёк
        // Это предотвращает перезагрузку после редактирования визуальных параметров метрики
        if (skipTableFetchUntil > Date.now()) {
            devLog('[ReportTable] BLOCKED by skipTableFetchUntil');
            return;
        }
        
        // Блокируем загрузку если нет группировок или метрик
        if (!groupings || groupings.length === 0 || !metrics || metrics.length === 0) {
            devLog('[ReportTable] BLOCKED - groupings or metrics is empty', { groupings: groupings?.length, metrics: metrics?.length, blockId });
            // Сбрасываем флаги загрузки, чтобы не было бесконечного ожидания
            if (effectiveTableFetching) {
                if (isModal && !blockId) {
                    setMetaModalTableFetching({ value: false });
                } else {
                    // Для блоков передаём blockId, чтобы сбросить флаг конкретного блока
                    setMetaTableFetching({ value: false, blockId });
                }
                setMetaTableLoading({ value: false, blockId });
            }
            return;
        }
        
        if (isCompare) {
            if (!isTableCompareFetching) return;
            devLog('[ReportTable] loadCompareRows()');
            loadCompareRows();
            setMetaCompareTableFetching({ value: false });
        } else {
            if (!effectiveTableFetching) return;
            // Отмечаем, что компонент обновился (для совместимости с legacy-кодом)
            if (!didUpdate) {
                handleUpdate();
            }
            devLog('[ReportTable] loadRows()');
            loadRows();
            // «Динамика» включена — пересчитываем и прошлый период
            if (isAnalysisRef.current) {
                loadAnalysisRows();
            }
            // Спарклайны: после перезагрузки таблицы запросим линии для новых строк
            if (isSparkActiveRef.current) {
                resetSparklines(tableData.data);
            }
            // Сбрасываем правильный флаг в зависимости от контекста
            if (isModal && !blockId) {
                setMetaModalTableFetching({ value: false });
            } else {
                // Для блоков передаём blockId, чтобы сбросить флаг конкретного блока
                setMetaTableFetching({ value: false, blockId });
            }
        }
    // ВАЖНО: убираем metrics и groupings из зависимостей, чтобы изменение визуальных параметров
    // (название, цвет, план) не триггерило перезагрузку данных отчёта.
    // Перезагрузка происходит только при изменении effectiveTableFetching (через setMetaTableFetching или setMetaModalTableFetching)
    // skipTableFetchUntil не добавляем в зависимости - он используется только для блокировки
    }, [effectiveTableFetching, isTableCompareFetching, triggerUpdate]);

    // Интервал для автоскролла при раскрытии всех строк
    // ВАЖНО: НЕ устанавливаем metricsChecked здесь — это делается в useReportLoader.ts
    // при загрузке сохранённого отчёта или дефолтных настроек
    useEffect(() => {
        const intervalId = setInterval(() => {
            const lastLoadDateTimeStr = safeLocalStorage.getItem('lastLoadDateTime');
            if (lastLoadDateTimeStr && isAllExpandedLoading) {
                const { time, completed } = JSON.parse(lastLoadDateTimeStr);
                const lastLoadDateTime = new Date(time);
                const now = new Date();
                const diffInSeconds = (+now - +lastLoadDateTime) / 1000;
                const element = document.querySelector('.BaseTable__body');
                if (element) {
                    const percent = element.scrollHeight * 0.05;
                    const scrollPositionCondition = Math.ceil(element.scrollTop + element.clientHeight) < element.scrollHeight;
                    if (scrollPositionCondition && completed) {
                        element.scrollBy({ top: percent, behavior: 'smooth' });
                    } else {
                        if (diffInSeconds >= 1.5 && completed) {
                            safeLocalStorage.removeItem('lastLoadDateTime');
                            setIsAllExpandedLoading({ value: false });
                        }
                    }
                }
                else {
                    if (diffInSeconds >= 1.5 && completed) {
                        safeLocalStorage.removeItem('lastLoadDateTime');
                        setIsAllExpandedLoading({ value: false });
                    }
                }
            }
        }, 500);
        return () => clearInterval(intervalId);
    }, [isAllExpandedLoading]);

    useEffect(() => {
        if (isCompactMode) {
            // Для compact режима вычисляем высоту из реального родительского контейнера
            const calculateHeight = () => {
                if (!tableDivRef.current) {
                    setDimensionsTable({ height: 200 });
                    return;
                }

                // Измеряем реальную высоту родительского div (контейнер с getStyles)
                const parentDiv = tableDivRef.current.parentElement as HTMLElement;
                if (parentDiv && parentDiv.clientHeight > 0) {
                    setDimensionsTable({ height: Math.max(100, parentDiv.clientHeight) });
                    return;
                }

                // Фоллбэк: вычисляем из grid-item
                const gridItem = tableDivRef.current.closest('.react-grid-item') as HTMLElement;
                if (gridItem) {
                    const blockHeight = gridItem.offsetHeight;
                    const tableHeight = blockHeight - 75;
                    setDimensionsTable({ height: Math.max(100, tableHeight) });
                } else {
                    setDimensionsTable({ height: 200 });
                }
            };
            
            // Запускаем после рендера
            requestAnimationFrame(calculateHeight);
            const timeoutId = setTimeout(calculateHeight, 100);
            
            // ResizeObserver для изменения размера блока
            let resizeObserver: ResizeObserver | null = null;
            if (tableDivRef.current) {
                const gridItem = tableDivRef.current.closest('.react-grid-item');
                if (gridItem) {
                    resizeObserver = new ResizeObserver(calculateHeight);
                    resizeObserver.observe(gridItem);
                }
            }
            
            return () => {
                clearTimeout(timeoutId);
                resizeObserver?.disconnect();
            };
        } else {
            const docEl = document.documentElement;
            setDimensionsTable({
                height: docEl.clientHeight - TABLE_HEIGHT_OFFSET,
            });
        }
    }, [tableWidth, isCompactMode]);

    const isGroupsChanged = useRef(false);

    // Функция расчета оптимальной ширины колонки метрики
    // Ширина столбца такая, чтобы название помещалось в 2 строки без обрезки
    // При этом ширина >= ширины самого длинного слова (слова не разрываются)
    const calculateMetricColumnWidth = useCallback((metricName: string): number => {
        // Приводим к строке для безопасной работы с методами строк
        const safeMetricName = String(metricName ?? '');
        if (!safeMetricName) {
            return COL_WIDTH;
        }
        
        const padding = 55; // Отступы для иконок, кнопок и padding
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        if (context) {
            context.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            
            // Находим ширину самого длинного слова (слова не должны разрываться)
            const words = safeMetricName.split(/\s+/);
            let maxWordWidth = 0;
            words.forEach((word) => {
                const wordWidth = context.measureText(word).width;
                if (wordWidth > maxWordWidth) {
                    maxWordWidth = wordWidth;
                }
            });
            
            // Минимальная ширина = ширина самого длинного слова + padding (с запасом 15%)
            const minWidthForLongestWord = Math.ceil(maxWordWidth * 1.15) + padding;
            
            // Ширина для размещения всего текста в 2 строки (с запасом 15%)
            const totalTextWidth = context.measureText(safeMetricName).width;
            const widthFor2Lines = Math.ceil((totalTextWidth * 1.15) / 2) + padding;
            
            // Берём максимум: либо под длинное слово, либо под 2 строки
            const calculatedWidth = Math.max(minWidthForLongestWord, widthFor2Lines);
            
            return Math.max(COL_MIN_WIDTH, Math.min(calculatedWidth, COL_MAX_WIDTH));
        }
        
        // Fallback без canvas
        const charWidth = 10;
        const words = safeMetricName.split(/\s+/);
        const maxWordLength = Math.max(...words.map((w) => w.length));
        const minWidthForLongestWord = Math.ceil(maxWordLength * charWidth * 1.15) + padding;
        const widthFor2Lines = Math.ceil((safeMetricName.length * charWidth * 1.15) / 2) + padding;
        const calculatedWidth = Math.max(minWidthForLongestWord, widthFor2Lines);
        
        return Math.max(COL_MIN_WIDTH, Math.min(calculatedWidth, COL_MAX_WIDTH));
    }, []);

    // Функция расчета оптимальной ширины колонки на основе группировок
    const calculateOptimalWidth = useCallback((currentGroupings: GroupingItem[]): number => {
        if (currentGroupings.length === 0) {
            return COL_GROUP_MINWIDTH;
        }
        
        const groupingsText = currentGroupings
            .map((item: GroupingItem) => item?.label || item?.name || item?.dataKey)
            .filter(Boolean)
            .join(' → ');
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
            context.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            const textWidth = context.measureText(groupingsText).width;
            const maxLines = 3;
            const padding = 60;
            const calculatedWidth = Math.ceil((textWidth * 1.1) / maxLines) + padding;
            return Math.max(COL_GROUP_MINWIDTH, Math.min(calculatedWidth, COL_MAX_WIDTH));
        }
        
        // Fallback
        const charWidth = 9;
        const calculatedWidth = Math.ceil((groupingsText.length * charWidth) / 3) + 60;
        return Math.max(COL_GROUP_MINWIDTH, Math.min(calculatedWidth, COL_MAX_WIDTH));
    }, []);

    // Используем сохранённую ширину для блоков дашборда, иначе вычисляем оптимальную
    const [columnWidth, setColumnWidth] = useState<number>(() => {
        const numSavedWidth = Number(savedColumnWidth);
        if (isDashboardBlock && numSavedWidth > 0) {
            return numSavedWidth;
        }
        return calculateOptimalWidth(groupings);
    });

    // Синхронизация savedColumnWidth из Redux (может прийти после маунта через loadBlockTableData)
    const appliedSavedColumnWidthRef = useRef(false);
    useEffect(() => {
        const numSavedWidth = Number(savedColumnWidth);
        if (isDashboardBlock && numSavedWidth > 0 && !appliedSavedColumnWidthRef.current) {
            appliedSavedColumnWidthRef.current = true;
            setColumnWidth(numSavedWidth);
        }
    }, [isDashboardBlock, savedColumnWidth]);

    // Debounced версия установки ширины столбца
    const debouncedSetColumnWidth = useDebounce((width: number) => {
        // Только увеличиваем ширину, никогда не уменьшаем автоматически
        setColumnWidth((prev) => {
            const newWidth = width > COL_GROUP_MINWIDTH ? width : COL_GROUP_MINWIDTH;
            const finalWidth = newWidth > prev ? newWidth : prev;
            // Сохраняем в Redux для блоков дашборда
            if (isDashboardBlock && blockId && finalWidth !== prev) {
                loadBlockTableData({ blockId, data: { columnWidth: finalWidth } });
            }
            return finalWidth;
        });
    }, DEBOUNCE_RESIZE_MS);

    const handleCellWidthChange = useCallback((width: number) => {
        if (isGroupsChanged.current) {
            isGroupsChanged.current = false;
            return;
        }
        // Используем debounce для изменения ширины при drag
        // Ширина может только увеличиваться, не уменьшаться
        debouncedSetColumnWidth(width);
    }, [debouncedSetColumnWidth]);

    // Ref для отслеживания предыдущего текста группировок (для сравнения)
    const prevGroupingsTextRef = useRef<string>('');

    // Сброс ширины столбца при смене отчёта
    useEffect(() => {
        // При смене типа отчета сбрасываем ref и пересчитываем ширину
        prevGroupingsTextRef.current = '';
        const optimalWidth = calculateOptimalWidth(groupings);
        setColumnWidth(optimalWidth);
        isGroupsChanged.current = true;
    }, [reportType]);

    useEffect(() => {
        // Получаем текущий текст группировок для сравнения
        const currentGroupingsText = groupings
            .map((item: GroupingItem) => item?.label || item?.name || item?.dataKey)
            .filter(Boolean)
            .join(' → ');

        // Обновляем ширину только если текст группировок реально изменился
        if (currentGroupingsText !== prevGroupingsTextRef.current) {
            const isInitialLoad = prevGroupingsTextRef.current === '';
            prevGroupingsTextRef.current = currentGroupingsText;

            // При первой загрузке блока дашборда — не перезаписываем сохранённую ширину
            if (isInitialLoad && isDashboardBlock && Number(savedColumnWidth) > 0) {
                isGroupsChanged.current = true;
                return;
            }

            const optimalWidth = calculateOptimalWidth(groupings);
            setColumnWidth(optimalWidth);
            isGroupsChanged.current = true;
        }
    }, [groupings, calculateOptimalWidth, isDashboardBlock, savedColumnWidth]);

    const [columnsWidth, setColumnsWidth] = useState<Record<string, number>>(() => {
        if (isDashboardBlock && savedColumnsWidth && typeof savedColumnsWidth === 'object') {
            const normalized: Record<string, number> = {};
            for (const key of Object.keys(savedColumnsWidth)) {
                normalized[key] = Number(savedColumnsWidth[key]) || 0;
            }
            return normalized;
        }
        return {};
    });

    // Синхронизация savedColumnsWidth из Redux (может прийти после маунта через loadBlockTableData)
    const appliedSavedColumnsWidthRef = useRef(false);
    useEffect(() => {
        if (isDashboardBlock && savedColumnsWidth && typeof savedColumnsWidth === 'object'
            && Object.keys(savedColumnsWidth).length > 0 && !appliedSavedColumnsWidthRef.current) {
            appliedSavedColumnsWidthRef.current = true;
            const normalized: Record<string, number> = {};
            for (const key of Object.keys(savedColumnsWidth)) {
                normalized[key] = Number(savedColumnsWidth[key]) || 0;
            }
            setColumnsWidth(normalized);
        }
    }, [isDashboardBlock, savedColumnsWidth]);

    useEffect(() => {
        if (isCompare) {
            const calculateMaxWidth = (): Record<string, number> => {
                const result: Record<string, number> = {};
                const elements = document.getElementsByClassName('column-span');

                for (let i = 0; i < elements.length; i++) {
                    const className = elements[i].classList[1];
                    result[className] = Math.max(elements[i].getBoundingClientRect().width, result[className] ?? 0);
                }
                for (const key in result) {
                    result[key] = result[key] + 40;
                }
                return result;
            };
            const width = calculateMaxWidth();
            setColumnsWidth(width);
        }
    }, [isChartFetching, isTableLoading]);

    // Обработчик изменения ширины столбца (drag resize)
    const handleColumnResizeEnd = useCallback(({ column, width }: { column: { dataKey: string }; width: number }) => {
        if (!column?.dataKey) return;

        // Колонка группировок — обновляем columnWidth
        if (column.dataKey === 'group') {
            const finalWidth = Math.max(COL_GROUP_MINWIDTH, width);
            setColumnWidth(finalWidth);
            if (isDashboardBlock && blockId) {
                loadBlockTableData({ blockId, data: { columnWidth: finalWidth } });
            }
            return;
        }

        // Колонки метрик — обновляем columnsWidth
        setColumnsWidth((prev) => {
            const updated = { ...prev, [column.dataKey]: width };
            if (isDashboardBlock && blockId) {
                loadBlockTableData({ blockId, data: { columnsWidth: updated } });
            }
            return updated;
        });
    }, [isDashboardBlock, blockId, loadBlockTableData]);

    useLayoutEffect(() => {
        const _data = finishRows({
            rows: tableData.data,
            filters: filtersTable,
            sort: sortTable,
            percentages,
            totalRow: tableData.total,
            // Используем синхронно вычисленные метрики для мгновенного обновления
            totalChoosenRow: hideTotalChosenRow ? undefined : computedTotalChosenMetrics
        });
        const temp = _data.map((el: TableRowData) => {
            const newEl = { ...el };
            if (newEl.pathStr === TOTAL_PATH_STR) {
                newEl.pathStr2 = TOTAL_PATH_STR;
            } else if (newEl.group === EMPTY_GROUP) {
                newEl.pathStr2 = uuidv4();
            } else if (newEl.pathStr === TOTAL_CHOSEN_PATH_STR) {
                newEl.pathStr2 = TOTAL_CHOSEN_PATH_STR;
            }
            return { ...newEl, uniqueKey: uuidv4() };
        });
        dataRef.current.data = temp;
        // Принудительно обновляем renderTable useMemo после изменения данных
        triggerUpdate();
    }, [
        tableData.data,
        tableData.total,
        computedTotalChosenMetrics,
        filtersTable,
        sortTable,
        percentages,
        expandedRowKeys,
    ]);

    // Мемоизированное вычисление maxValues для заливки ячеек
    const maxValuesMap = useMemo<MaxValuesMap>(() => {
        const result: MaxValuesMap = {};
        
        if (!isFilling || !dataRef.current.data.length) {
            return result;
        }

        // Собираем все dataKey метрик
        const metricKeys = metrics.map((m) => m?.dataKey).filter(Boolean);
        
        // Один проход по данным для вычисления всех max/min значений
        dataRef.current.data
            .filter((rowItem: TableRowData) => 
                rowItem.pathStr !== TOTAL_PATH_STR && rowItem.pathStr !== TOTAL_CHOSEN_PATH_STR
            )
            .forEach((rowItem: TableRowData) => {
                metricKeys.forEach((dataKey) => {
                    if (!dataKey) return;
                    const numericValue = Number(rowItem[dataKey]);
                    if (!isNaN(numericValue)) {
                        if (result[dataKey]) {
                            result[dataKey].maxPositive = Math.max(result[dataKey].maxPositive, numericValue);
                            result[dataKey].maxNegative = Math.min(result[dataKey].maxNegative, numericValue);
                        } else {
                            result[dataKey] = {
                                maxNegative: numericValue >= 0 ? 0 : numericValue,
                                maxPositive: numericValue >= 0 ? numericValue : 0,
                            };
                        }
                    }
                });
            });
        
        return result;
    }, [isFilling, dataRef.current.data, metrics, updateTrigger]);

    // Группы метрик, для которых показывается среднее арифметическое (серый цвет + звёздочка)
    const averageAxisGroups = useMemo(() => [5, 6, 7, 8, 10], []);
    
    // Тултип для метрик со средним значением
    const averageTooltipText = 'Это арифметическое среднее значение, рассчитанное по данным из таблиц. Оно может отличаться от фактического значения.';
    
    // Обёртка для ячеек со средним значением в строке "Итого и среднее выбранных"
    const wrapWithAverageStyle = useCallback((content: React.ReactNode, isAverageMetric: boolean) => {
        if (!isAverageMetric) return content;
        
        return (
            <Tooltip title={averageTooltipText} placement="top">
                <span style={{ color: '#888', cursor: 'help', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%' }}>
                    {content}<span style={{ marginLeft: '2px' }}>*</span>
                </span>
            </Tooltip>
        );
    }, []);

    // Рендеринг ячейки без заливки (isFilling = false)
    const renderNormalCell = useCallback((renderItem: CellRenderProps, dataKey: string, item: MetricItem) => {
        const cellData = renderItem.cellData as string | number;
        const rowData = renderItem.rowData;
        
        // Проверяем, является ли это строкой "Итого и среднее выбранных" и метрикой из групп 5, 6, 7, 8, 10
        const isTotalChosenRow = rowData?.pathStr === TOTAL_CHOSEN_PATH_STR || rowData?.pathStr2 === TOTAL_CHOSEN_PATH_STR;
        
        // Для кастомных метрик используем расширенную функцию определения группы
        const axisGroup = dataKey.startsWith('_') 
            ? getAxisGroupForCustomMetric({
                base_metric_name: (item as any)?.base_metric_name,
                metric_formula: (item as any)?.metric_formula,
                unit: (item as any)?.unit,
                dataKey
            })
            : getAxisGroupForMetric(dataKey);
        const isAverageMetric = isTotalChosenRow && averageAxisGroups.includes(axisGroup);

        // Для кастомных метрик (есть настройки заливки/фона) всегда используем общий рендер,
        // иначе ранние return (percent/money/convert) обходят заливку.
        if (
            (item as any)?.fillingConditions?.length ||
            (item as any)?.background ||
            (item as any)?.isFilling === true ||
            (item as any)?.isFilling === false
        ) {
            const customContent = getCustomMetricRow(dataKey, renderItem, item, currency, dataRef.current.data);
            return wrapWithAverageStyle(customContent, isAverageMetric);
        }
        
        if (checkPercentDataType(dataKey)) {
            if (isAverageMetric) {
                return (
                    <Tooltip title={averageTooltipText} placement="top">
                        <span style={{ cursor: 'help', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px', color: '#888' }}>
                            {formatConvertData(cellData, dataKey)}<span style={{ marginLeft: '2px' }}>*</span>
                        </span>
                    </Tooltip>
                );
            }
            return (
                <span style={{ textAlign: 'right', width: '100%', paddingRight: '4px' }}>
                    {formatConvertData(cellData, dataKey)}
                </span>
            );
        }
        if (dataKey === 'ads_spend') {
            if (cellData) {
                if (isAverageMetric) {
                    return (
                        <Tooltip title={averageTooltipText} placement="top">
                            <span style={{ cursor: 'help', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px', color: '#888' }}>
                                {formatMoneyData(cellData, currency, dataKey)}<span style={{ marginLeft: '2px' }}>*</span>
                            </span>
                        </Tooltip>
                    );
                }
                return (
                    <span style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px' }}>
                        {formatMoneyData(cellData, currency, dataKey)}
                    </span>
                );
            }
        }
        if (checkMoneyDataType(dataKey)) {
            if (isAverageMetric) {
                return (
                    <Tooltip title={averageTooltipText} placement="top">
                        <span style={{ cursor: 'help', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px', color: '#888' }}>
                            {formatMoneyData(cellData, currency, dataKey)}<span style={{ marginLeft: '2px' }}>*</span>
                        </span>
                    </Tooltip>
                );
            }
            return (
                <span style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px' }}>
                    {formatMoneyData(cellData, currency, dataKey)}
                </span>
            );
        }
        if (checkConvertDataType(dataKey)) {
            if (isAverageMetric) {
                return (
                    <Tooltip title={averageTooltipText} placement="top">
                        <span style={{ cursor: 'help', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', height: '100%', paddingRight: '4px', color: '#888' }}>
                            {formatConvertData(cellData, dataKey)}<span style={{ marginLeft: '2px' }}>*</span>
                        </span>
                    </Tooltip>
                );
            }
            return (
                <span style={{ textAlign: 'right', width: '100%', paddingRight: '4px' }}>
                    {formatConvertData(cellData, dataKey)}
                </span>
            );
        }
        const customContent = getCustomMetricRow(dataKey, renderItem, item, currency, dataRef.current.data);
        return wrapWithAverageStyle(customContent, isAverageMetric);
    }, [currency, averageAxisGroups, wrapWithAverageStyle]);

    // Вычисление стиля заливки для ячейки (использует мемоизированный maxValuesMap)
    const getFillingStyle = useCallback((renderItem: CellRenderProps, dataKey: string): Properties => {
        const style: Properties = {
            width: '100%',
            backgroundImage: 'unset',
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            height: `${CELL_HEIGHT}px`,
            paddingRight: '4px'
        };

        // Применяем заливку если не итоговая строка
        const isNotTotalRow = renderItem.rowData.group !== TOTAL_LABEL && 
                              renderItem.rowData.pathStr !== TOTAL_PATH_STR && 
                              renderItem.rowData.pathStr !== TOTAL_CHOSEN_PATH_STR;
        
        if (isNotTotalRow && maxValuesMap[dataKey]) {
            const maxValue = Math.max(
                maxValuesMap[dataKey].maxPositive, 
                Math.abs(maxValuesMap[dataKey].maxNegative)
            );
            if (maxValue > 0) {
                const cellValue = Number(renderItem.cellData);
                const percent = (Math.abs(cellValue) / maxValue) * 100;
                const checkSign = (value: number) => (value > 0 ? '#9BDD9B' : '#ED9B9B');
                const fillPercent = Math.abs(cellValue) !== 0 && percent < 5 ? 5 : percent;
                style.backgroundImage = `linear-gradient(to left, ${checkSign(cellValue)} ${fillPercent}%, transparent ${fillPercent}%)`;
            }
        }

        return style;
    }, [maxValuesMap]);

    // Рендеринг ячейки с заливкой (isFilling = true)
    const renderFillingCell = useCallback((renderItem: CellRenderProps, dataKey: string, item: MetricItem) => {
        const fillingStyle = getFillingStyle(renderItem, dataKey);
        
        // Получаем содержимое ячейки через обычный рендеринг
        const normalContent = renderNormalCell(renderItem, dataKey, item);
        
        // Если normalContent - это уже span, клонируем его с добавлением стиля заливки
        if (normalContent && typeof normalContent === 'object' && 
            (normalContent as React.ReactElement).type === 'span') {
            const element = normalContent as React.ReactElement;
            const existingStyle = element.props?.style || {};
            return (
                <span style={{ ...existingStyle, ...fillingStyle }}>
                    {element.props?.children}
                </span>
            );
        }
        
        // Если это что-то другое (например, из getCustomMetricRow), оборачиваем в div с заливкой
        return (
            <div style={fillingStyle}>
                {normalContent}
            </div>
        );
    }, [getFillingStyle, renderNormalCell]);

    // «Динамика»: подпись изменения к прошлому периоду («+33.33%») под числом ячейки, null — не показываем.
    // Всё состояние — из ref (см. analysisDataRef), поэтому функция одна на весь жизненный цикл
    const renderAnalysisBadge = useCallback((renderItem: CellRenderProps, dataKey: string, item: MetricItem): React.ReactNode => {
        const rowData = renderItem.rowData;
        if (!isAnalysisRef.current || !rowData || rowData.group === EMPTY_GROUP) return null;
        const analysisData = analysisDataRef.current;
        const analysisChosenMetrics = analysisChosenRef.current;
        const flatMode = flatModeRef.current;

        let prevValue: unknown;
        if (rowData.pathStr === TOTAL_PATH_STR) {
            if (!analysisData.total) return null;
            prevValue = analysisData.total[dataKey];
        } else if (rowData.pathStr === TOTAL_CHOSEN_PATH_STR) {
            if (!analysisChosenMetrics) return null;
            prevValue = analysisChosenMetrics[dataKey];
        } else {
            const path = rowData.clientData?.path;
            if (!path) return null;
            // Уровень за прошлый период ещё не загружен — пока без подписи
            const parentKey = flatMode ? '' : path.slice(0, -1).join('-');
            if (!analysisData.loadedParents[parentKey]) return null;
            const prevRow = analysisData.rows[buildAnalysisRowKey(path, rowData.group, flatMode)];
            // Строки не было в прошлом периоде — было 0
            prevValue = prevRow ? prevRow[dataKey] : 0;
        }

        const current = parseMetricValue(renderItem.cellData);
        const previous = parseMetricValue(prevValue);
        if (current === null || previous === null) return null;

        const meta = {
            base_metric_name: item?.base_metric_name,
            metric_formula: item?.metric_formula,
            unit: typeof item?.unit === 'string' ? item.unit : undefined,
        };
        const isPercent = isPercentMetric(dataKey, meta);
        const change = calcMetricChange(current, previous, isPercent);
        const tone = getTrendTone(getMetricTrendDirection(dataKey, meta), change.delta);
        const previousText = `${previous.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}${isPercent ? '%' : ''}`;

        return (
            <span
                className={`report-analysis-badge report-analysis-badge--${tone}`}
                title={`Прошлый период (${analysisData.periodLabel}): ${previousText}`}
            >
                {formatMetricChange(change)}
            </span>
        );
    }, []);

    // Спарклайн ячейки: линия по отрезкам дат у левого края, null — не показываем.
    // Как и renderAnalysisBadge, всё читает из ref — функция одна на весь жизненный цикл
    const renderSparkline = useCallback((renderItem: CellRenderProps, dataKey: string): React.ReactNode => {
        if (!isSparkActiveRef.current || !sparkKeysRef.current.includes(dataKey)) return null;
        const rowData = renderItem.rowData;
        if (!rowData || rowData.group === EMPTY_GROUP) return null;
        const data = sparkDataRef.current;

        let series: Array<number | null> | undefined;
        let isPending = false;
        if (rowData.pathStr === TOTAL_CHOSEN_PATH_STR) {
            series = sparkChosenRef.current?.[dataKey];
        } else if (rowData.pathStr === TOTAL_PATH_STR) {
            series = data.total?.[dataKey];
            isPending = data.totalPending;
        } else if (rowData.clientData?.path) {
            const rowKey = buildAnalysisRowKey(rowData.clientData.path, null, false);
            series = data.rows[rowKey]?.[dataKey];
            isPending = Boolean(data.pending[rowKey]);
        }
        // Линии ещё нет, но она грузится — заглушка вместо пустоты
        if (!series) return isPending ? <SparklineLoading /> : null;
        if (data.labels.length === 0) return null;
        const values = series.filter((value): value is number => value !== null && Number.isFinite(value));
        // Все точки пустые — метрика не поддерживает разбивку по датам
        if (values.length === 0) return null;

        // Значение точки показывает сам спарклайн при наведении мышью (просьба Дениса)
        return (
            <Sparkline values={series} />
        );
    }, []);

    // Унифицированная функция создания столбцов (устранение дублирования)
    const createTableColumns = useCallback((): ColumnConfig[] => {
        // Столбец группировки
        const groupColumn: ColumnConfig = {
            title: 'Группа',
            dataKey: 'group',
            key: 'group',
            width: columnWidth,
            minWidth: COL_GROUP_MINWIDTH,
            maxWidth: COL_MAX_WIDTH,
            frozen: 'left',
            resizable: true,
            groupings: groupings as GroupingItem[],
            sortTable,
            isFilling,
            loadRowKeys,
            columnWidth,
            headerRenderer: () => <GroupHeader sort={sortTable} dataKey={'group'} hideSelectionControls={hideSelectionControls} blockId={blockId} isModal={isModal} />,
            cellRenderer: ({ rowData }: CellRenderProps) => (
                <GroupCell
                    rowData={rowData}
                    expandedRowKeys={expandedRowKeys}
                    onCellWidthChange={handleCellWidthChange}
                    hideTotalCheckbox={hideTotalCheckbox}
                    hideRowCheckboxes={hideRowCheckboxes}
                    forceMultiSelect={disableGroupsLimit}
                    blockId={blockId}
                    isModal={isModal}
                />
            ),
        };

        // Столбцы метрик (фильтруем элементы без dataKey)
        const metricColumns: ColumnConfig[] = (metrics as MetricItem[])
            .filter((item) => item && item.dataKey)
            .map((item) => {
            const { name, description, dataKey } = item;
            // Рассчитываем оптимальную ширину на основе названия метрики (макс 2 строки заголовка)
            const optimalMetricWidth = calculateMetricColumnWidth(name);
            const metricWidth = isFilling 
                ? optimalMetricWidth 
                : Math.max(optimalMetricWidth, columnsWidth[dataKey] || COL_WIDTH);

            return {
                title: name,
                description,
                dataKey,
                key: dataKey,
                background: item.background,
                item,
                plan: item.plan,
                base_metric_name: item.base_metric_name,
                metric_formula: item.metric_formula,
                width: metricWidth + (analysisExtraWidth[dataKey] ?? 0),
                minWidth: COL_MIN_WIDTH,
                maxWidth: COL_MAX_WIDTH,
                resizable: true,
                filtersTable,
                sortTable,
                columnsWidth: isFilling ? undefined : columnsWidth,
                frozen: frozensColumns?.[dataKey] || false,
                headerRenderer: ({ column }: { column?: ColumnConfig }) => (
                    <HeaderCell
                        column={column}
                        filters={filtersTable}
                        sort={sortTable}
                        setFrozens={setFrozensColumns}
                        item={item}
                        hideMetricButtons={hideMetricButtons}
                        showTableSettings={showTableSettings}
                        isCompactMode={isCompactMode}
                        blockId={blockId}
                    />
                ),
                cellRenderer: (renderItem: CellRenderProps) => {
                    const content = isFilling
                        ? renderFillingCell(renderItem, dataKey, item)
                        : renderNormalCell(renderItem, dataKey, item);
                    const badge = renderAnalysisBadge(renderItem, dataKey, item);
                    const sparkline = renderSparkline(renderItem, dataKey);
                    // Макет: спарклайн у левого края, справа столбик — число, под ним изменение «Динамики»
                    const cell = !badge && !sparkline ? content : (
                        <div
                            className={`report-analysis-cell${isFilling ? ' report-analysis-cell--filling' : ''}`}
                            data-analysis-key={dataKey}
                        >
                            {sparkline}
                            <div className="report-analysis-cell__stack">
                                <div className="report-analysis-cell__value">{content}</div>
                                {badge}
                            </div>
                        </div>
                    );
                    // Заливка по условиям столбца — только обычные метрики (у кастомных она своя, в настройках метрики)
                    // и не поверх общей заливки таблицы (кнопка над таблицей)
                    const fillStyle = isFilling || dataKey.startsWith('_')
                        ? null
                        : getCellFillStyle(cellFillsRef.current[dataKey], dataKey, renderItem, dataRef.current.data);
                    if (!fillStyle) return cell;
                    return <div className="report-cell-fill" style={fillStyle}>{cell}</div>;
                },
            };
        });

        return [groupColumn, ...metricColumns];
    }, [
        renderAnalysisBadge,
        renderSparkline,
        analysisExtraWidth,
        columnWidth,
        groupings,
        sortTable,
        isFilling,
        loadRowKeys,
        expandedRowKeys,
        handleCellWidthChange,
        metrics,
        columnsWidth,
        filtersTable,
        frozensColumns,
        renderFillingCell,
        renderNormalCell,
        calculateMetricColumnWidth,
        showTableSettings,
    ]);

    // Обновление столбцов таблицы
    useEffect(() => {
        const columns = createTableColumns();
        setColumnsTable(columns);
    }, [createTableColumns]);

    // Скрытую таблицу (предпросмотр окна блока) показали — ширину колонок под линию/подпись
    // замеряем заново: пока она была скрыта, замер пропускался
    useEffect(() => {
        const root = tableDivRef.current;
        if (!root || typeof ResizeObserver === 'undefined') return undefined;
        let wasHidden = root.offsetWidth === 0;
        const observer = new ResizeObserver(() => {
            const hidden = root.offsetWidth === 0;
            if (wasHidden && !hidden && (isAnalysisRef.current || isSparkActiveRef.current)) {
                triggerUpdate();
            }
            wasHidden = hidden;
        });
        observer.observe(root);
        return () => observer.disconnect();
    }, [ triggerUpdate ]);

    // «Динамика»: колонку, где подпись и число не помещаются рядом, расширяем ровно на нехватку.
    // Замер по DOM после отрисовки; пока «Динамика» включена, ширина только растёт (без качелей)
    // Спарклайны — то же самое: подпись/линия/число в ячейке должны помещаться в одну строку
    useEffect(() => {
        if (!isAnalysis && !isSparkActive) {
            setAnalysisExtraWidth((prev) => (Object.keys(prev).length ? {} : prev));
            return undefined;
        }
        const root = tableDivRef.current;
        if (!root) return undefined;
        const frame = requestAnimationFrame(() => {
            // Скрытая таблица (предпросмотр в окне блока до «Предпросмотр») — у ячеек ширина 0,
            // и замер при каждом обновлении прибавлял бы колонке ширину без конца
            if (root.offsetWidth === 0) return;
            const lack: Record<string, number> = {};
            root.querySelectorAll<HTMLElement>('.report-analysis-cell[data-analysis-key]').forEach((cell) => {
                const key = cell.dataset.analysisKey;
                // Части ячейки: спарклайн (svg — у него нет offsetWidth) и столбик «число + изменение».
                // Одного столбика хватает для замера: подпись под числом бывает шире колонки
                const parts = Array.from(cell.children);
                if (!key || parts.length === 0) return;
                const valuePart = parts[parts.length - 1];
                // offsetWidth, а не getBoundingClientRect: в блоках дашборда стоит CSS zoom, а clientWidth
                // ячейки — без него; у svg offsetWidth нет, его ширина известна заранее.
                // Спарклайн (и линия, и заглушка) — вместе с отступом слева: offsetWidth margin не включает
                const partWidth = (part: Element) => (part.classList.contains('report-sparkline')
                    ? SPARKLINE_WIDTH + SPARKLINE_INDENT
                    : (part as HTMLElement).offsetWidth);
                const partsWidth = parts.reduce((sum, part) => sum + partWidth(part), 0);
                const valueOverflow = valuePart instanceof HTMLElement
                    ? Math.max(0, valuePart.scrollWidth - valuePart.offsetWidth)
                    : 0;
                const need = partsWidth + valueOverflow + (ANALYSIS_BADGE_GAP * (parts.length - 1)) - cell.clientWidth;
                if (need > 0) {
                    lack[key] = Math.max(lack[key] ?? 0, Math.ceil(need) + 2);
                }
            });
            if (Object.keys(lack).length === 0) return;
            setAnalysisExtraWidth((prev) => {
                const next = { ...prev };
                Object.entries(lack).forEach(([ key, px ]) => {
                    next[key] = (prev[key] ?? 0) + px;
                });
                return next;
            });
        });
        return () => cancelAnimationFrame(frame);
    }, [ isAnalysis, analysisData, analysisChosenMetrics, isSparkActive, sparkData, columnsTable, updateTrigger ]);

    // Принудительное обновление при изменении adsGroupsSelected
    useEffect(() => {
        triggerUpdate();
    }, [adsGroupsSelected, triggerUpdate]);

    // Оптимизированная функция обработки дочерних элементов (использует Set)
    const processChildren = useCallback((children: TableRowData[]) => {
        children.forEach((el) => {
            if (!Array.isArray(el.children)) return;
            const pathStr = el.pathStr2;
            
            if (!el?.clientData?.downloaded) {
                // Используем Set для O(1) проверки
                if (downloadedRowKeysSet.current.has(pathStr)) {
                    return;
                }
                downloadedRowKeysSet.current.add(pathStr);
                
                if (!loadRowKeysSet.current.has(pathStr)) {
                    setLoadRowKeys((prev) => [...prev, pathStr]);
                }
                if (!isCompare) {
                    safeSetTimeout(() => {
                        loadRows(el);
                        if (isAnalysisRef.current) {
                            loadAnalysisRowsRef.current(el);
                        }
                    }, EXPAND_DELAY_MS);
                }
                setMetaTableFetching({ value: false, blockId });
            } else {
                if (!expandedRowKeys.includes(pathStr)) {
                    setExpandedRowKeys((prev) => [...new Set([...prev, pathStr])]);
                }
            }

            if (el.children && el.children.length > 0) {
                processChildren(el.children);
            }
        });
    }, [isCompare, expandedRowKeys, setMetaTableFetching, safeSetTimeout]);

    // Оптимизированная функция обработки иконок раскрытия (использует Set)
    const handleExpandIconProps = useCallback(({ rowData }: { rowData: TableRowData }) => {
        try {
            if (rowData.pathStr2 === TOTAL_PATH_STR || rowData.pathStr2 === TOTAL_CHOSEN_PATH_STR) {
                return;
            }
            
            tableData.data.forEach((parentEl) => {
                const parentPathStr = parentEl.pathStr2;
                
                // Используем Set для O(1) проверки
                if (!expandedAllSet.current.has(parentPathStr)) return;
                
                (parentEl.children as TableRowData[])?.forEach((el) => {
                    const pathStr = el.pathStr2;
                    
                    if (!rowData?.clientData?.downloaded) {
                        // Используем Set для O(1) проверки
                        if (downloadedRowKeysSet.current.has(pathStr)) {
                            return;
                        }
                        downloadedRowKeysSet.current.add(pathStr);
                        
                        if (!loadRowKeysSet.current.has(pathStr)) {
                            setLoadRowKeys((prev) => [...new Set([...prev, pathStr])]);
                        }
                        if (!isCompare) {
                            if (!isAllExpandedLoading) {
                                setIsAllExpandedLoading({ value: true });
                            }
                            safeSetTimeout(() => {
                                if (!expandedRowKeys.includes(pathStr)) {
                                    loadRows(el);
                                    if (isAnalysisRef.current) {
                                        loadAnalysisRowsRef.current(el);
                                    }
                                }
                            }, EXPAND_DELAY_MS);
                        }
                        setMetaTableFetching({ value: false, blockId });
                    } else {
                        if (!expandedRowKeys.includes(pathStr)) {
                            setExpandedRowKeys((prev) => [...new Set([...prev, pathStr])]);
                        }
                    }
                    
                    if (Array.isArray(el.children)) {
                        processChildren(el.children);
                    }
                });
            });
        } catch (err) {
            console.error(err);
        }
    }, [tableData.data, isCompare, isAllExpandedLoading, expandedRowKeys, processChildren, setIsAllExpandedLoading, setMetaTableFetching, safeSetTimeout]);

    const handleCopyAllData = useCallback(() => {
        copyAllData(dataRef.current.data, groupings, metrics);
    }, [groupings, metrics]);

    // Окно блока дашборда: таблица в нём — предпросмотр за последние 7 дней, а копировать нужно
    // за период, выбранный на дашборде. Запрашиваем верхний уровень за этот период и копируем его
    const [ copyLoading, setCopyLoading ] = useState(false);
    const handleCopyDashboardPeriod = () => {
        if (!validateTableRequest(groupings, metrics, globalPeriod, 'ReportTable.copyDashboardPeriod')) return;
        const requestData = {
            period: {
                start: formatPeriodDate(globalPeriod.start, globalPeriod.withTime),
                end: formatPeriodDate(globalPeriod.end, globalPeriod.withTime),
            },
            filters: mergeAndBuildApiFilters({
                blockFilters: filters,
                globalDashboardFilters: dashboardFilters,
                isDashboardBlock: true,
            }),
            attribution: {
                attribution: typeof dashboardBlockAttributes.attribution === 'string'
                    ? [ dashboardBlockAttributes.attribution ]
                    : dashboardBlockAttributes.attribution,
                type_applications: typeof dashboardBlockAttributes.type_applications === 'string'
                    ? [ dashboardBlockAttributes.type_applications ]
                    : dashboardBlockAttributes.type_applications,
            },
            groupings_names: buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType)),
            metrics_names: metrics.map((col) => col?.dataKey).filter(Boolean),
            expanded_ids: [],
            flat_mode: false,
        };
        setCopyLoading(true);
        // Данные не получены — об этом уже сказали, «Ошибка при копировании» не показываем
        let loadFailed = false;
        const textPromise: Promise<string> = unApi
            .getUniversalReportTableData(requestData, location.pathname)
            .then((response) => {
                const resData = response.data;
                if (isDeletedMetricsError(resData) || isDeletedGroupsError(resData) || !resData?.success) {
                    throw new Error('copy: bad response');
                }
                const rows = prepareRows({
                    rows: (resData.rows ?? []).map((row: TableRowData) => (row.id ? { ...row, id: String(row.id) } : row)),
                    groupings,
                    flatMode: false,
                    groupFilter,
                });
                // Как в таблице: фильтры и сортировка столбцов, проценты, строка «Итого»
                const finished = finishRows({
                    rows,
                    filters: filtersTable,
                    sort: sortTable,
                    percentages,
                    totalRow: resData.total,
                });
                return buildAllTableDataText(finished, groupings, metrics);
            })
            .catch((error) => {
                loadFailed = true;
                if (error?.response?.status !== 401) {
                    showErrorMessage('Не удалось получить данные для копирования');
                }
                throw error;
            });
        // Запись в буфер начинается сразу в клике (Safari/Firefox не дают писать после ожидания ответа)
        copyToClipboardDeferred(textPromise)
            .then((success) => {
                if (!loadFailed) showCopyResult(success);
            })
            .finally(() => setCopyLoading(false));
    };


    // Выгрузка всех уровней группировок в фоне (кнопка слева от «Группировок» и в окне блока дашборда)
    const openExportModal = () => {
        // Модалка для фонового экспорта всех уровней группировок
        const today = new Date().toLocaleDateString('ru-RU');
        const blockTitle = getExportFileName?.().trim();
        let exportFileName = blockTitle ? `${blockTitle}_${today}` : `Отчёт_${today}`;
        let exportFlatMode = true;

        Modal.confirm({
            title: 'Выгрузить все уровни группировок',
            content: (
                <div>
                    <p style={{ marginBottom: 12 }}>
                        Отчёт будет сформирован в фоновом режиме.
                        Когда он будет готов, вы сможете скачать его
                        на странице «Экспорт». По готовности вам придёт
                        уведомление в Telegram.
                    </p>
                    <div style={{ marginBottom: 4 }}>Название файла:</div>
                    <Input
                        defaultValue={exportFileName}
                        placeholder="Введите название файла"
                        onChange={(e) => { exportFileName = e.target.value; }}
                        style={{ marginBottom: 12 }}
                    />
                    <div style={{ marginBottom: 4 }}>Тип выгрузки:</div>
                    <Radio.Group
                        defaultValue={true}
                        onChange={(e) => { exportFlatMode = e.target.value; }}
                    >
                        <Space direction="vertical">
                            <Radio value={true}>
                                <b>Развёрнутая</b>
                                <div style={{ color: '#888', fontSize: 12 }}>
                                    Когда в строках сразу показаны все конечные данные без промежуточных итогов.
                                </div>
                            </Radio>
                            <Radio value={false}>
                                <b>Многоуровневая</b>
                                <div style={{ color: '#888', fontSize: 12 }}>
                                    Когда у каждого уровня группировки есть своя итоговая строка.
                                </div>
                            </Radio>
                        </Space>
                    </Radio.Group>
                </div>
            ),
            okText: 'Выгрузить',
            cancelText: 'Отмена',
            onOk: () => {
                const groupings_names = buildGroupingsNames(groupings, groupFilter, groupSuffixByReportType(reportType));
                const metrics_names = metrics.map((col) => col?.dataKey).filter(Boolean);
                // headers схлопываем так же, как groupings_names: набор групп
                // регистрации = ОДИН заголовок «Группы регистраций» (custom_group_set),
                // иначе индексы уровней в CSV съезжают (лишний пустой столбец).
                const headers = [
                    ...buildDisplayGroupings(groupings, groupFilter).map((col) => ({ dataKey: col.dataKey, name: col.name })),
                    ...metrics.map((col) => ({ dataKey: col.dataKey, name: col.name })),
                ];

                // Окно блока дашборда показывает таблицу за последние 7 дней (предпросмотр),
                // а выгружаем блок как на дашборде: период и общие фильтры дашборда
                const exportPeriod = isEditingModal ? globalPeriod : period;
                const _period = {
                    start: formatPeriodDate(exportPeriod.start, exportPeriod.withTime),
                    end: formatPeriodDate(exportPeriod.end, exportPeriod.withTime),
                };

                const exportAttributes = isEditingModal ? dashboardBlockAttributes : attributes;
                const __attribution = exportAttributes?.attribution;
                const __type_app = exportAttributes?.type_applications;

                // Полный payload отчёта (как при «Применить») + headers
                const reportPayload = {
                    period: _period,
                    filters: mergeAndBuildApiFilters({
                        blockFilters: filters,
                        globalDashboardFilters: isEditingModal ? dashboardFilters : commonFilters,
                        isDashboardBlock: isDashboardBlock || isEditingModal,
                    }),
                    attribution: {
                        attribution:
                            typeof __attribution === 'string' ? [__attribution] : __attribution,
                        type_applications:
                            typeof __type_app === 'string' ? [__type_app] : __type_app,
                    },
                    groupings_names,
                    metrics_names,
                    expanded_ids: [],
                    flat_mode: exportFlatMode,
                    headers,
                };

                const exportData = {
                    school_id: selectedSchool?.id || '',
                    name: exportFileName || `Отчёт_${today}`,
                    values: JSON.stringify(reportPayload),
                };
                // Сервер по адресу страницы понимает тип отчёта (FilterController::getReportId); на
                // дашборде адрес /dashboards ему не знаком — берём адрес отчёта по типу таблицы блока
                const exportPathname = location.pathname.includes('dashboard')
                    ? REPORT_TYPE_PATHNAME[reportType] || location.pathname
                    : location.pathname;

                return unApi
                    .getExportTable(exportData, exportPathname)
                    .then((response) => {
                        if (response.status === 200) {
                            Modal.success({
                                title: 'Задача поставлена в очередь',
                                okText: 'Перейти к экспортам',
                                okCancel: true,
                                cancelText: 'Закрыть',
                                onOk: () => {
                                    window.open('/users/reports', '_blank');
                                },
                            });
                        }
                    })
                    .catch((error) => {
                        console.error('Ошибка при создании задачи экспорта:', error);
                        showErrorMessage('Не удалось создать задачу экспорта');
                    });
            },
        });
    };

    // В окне блока дашборда флаг загрузки графика общий для страницы и может висеть от блоков — не блокируем
    const isToolsBusy = isEditingModal ? false : isChartLoading;

    // Кнопка выгрузки (слева от «Группировок» в отчёте и в окне блока дашборда)
    const renderExportButton = (style?: React.CSSProperties) => (
        <Tooltip title="Выгрузить все уровни группировок">
            <Button
                type="default"
                className="report-icon-btn"
                style={style}
                onClick={openExportModal}
            >
                <DownloadOutlined />
            </Button>
        </Tooltip>
    );

    // Кнопки над таблицей справа: «Динамика», заливка, копирование (отчёт и окно блока дашборда)
    const renderToolButtons = () => (
        <>
            {/* «Динамика» — первая в группе кнопок (просьба Дениса «после Метрик»),
                но в той же группе: иначе на широкой таблице кнопки разъезжаются по краям */}
            {!isCompare && <Tooltip
                title={
                    <div className="page-title-tooltip-text">
                        {!analysisPeriod
                            ? `${ANALYSIS_BUTTON_NAME}: за всё время сравнивать не с чем — выберите другой период.`
                            : isAnalysis
                                ? `${ANALYSIS_BUTTON_NAME} к прошлому периоду: ${formatAnalysisPeriod(analysisPeriod, period.withTime)}. Нажмите, чтобы выключить.`
                                : `${ANALYSIS_BUTTON_NAME}: рост и падение каждой метрики к прошлому периоду (${formatAnalysisPeriod(analysisPeriod, period.withTime)}).`}
                        {analysisPeriod && (
                            <div style={{ marginTop: 8 }}>
                                <div><span className="report-analysis-badge report-analysis-badge--good">Зелёный</span> — хорошо</div>
                                <div><span className="report-analysis-badge report-analysis-badge--bad">Красный</span> — плохо</div>
                                <div><span className="report-analysis-badge report-analysis-badge--same">Синий</span> — без изменений или нейтрально</div>
                                <div style={{ marginTop: 4 }}>п.п. — на сколько пунктов изменился процент: было 50%, стало 60% — это +10 п.п.</div>
                            </div>
                        )}
                    </div>
                }
                color="#fff"
                zIndex={9999}
                placement="topLeft"
            >
                <Button
                    className={`report-icon-btn${isAnalysis ? ' report-icon-btn--active' : ''}`}
                    icon={<DynamicsIcon />}
                    type="default"
                    loading={analysisLoading}
                    disabled={isToolsBusy || !analysisPeriod}
                    onClick={handleAnalysisButton}
                />
            </Tooltip>}
            {!isCompare && <Tooltip
                title={<div className="page-title-tooltip-text">Заливка ячеек таблицы</div>}
                color="#fff"
                zIndex={9999}
                placement="top"
            >
                <Button
                    className={isFilling ? '' : 'report-icon-btn'}
                    icon={<BgColorsOutlined />}
                    type={isFilling ? 'primary' : 'default'}
                    disabled={isToolsBusy}
                    onClick={toggleFilling}
                />
            </Tooltip>}
            <Tooltip
                title={
                    <div className="page-title-tooltip-text">
                        {isEditingModal
                            ? `Копирование таблицы блока за период дашборда (${moment(globalPeriod.start).format('DD.MM.YYYY')} – ${moment(globalPeriod.end).format('DD.MM.YYYY')}) в буфер обмена, чтобы можно было вставить данные в Excel или Гугл-таблицы. В окне таблица показана за последние 7 дней — для предпросмотра.`
                            : 'Копирование данных таблицы в буфер обмена, чтобы можно было вставить данные в Excel или Гугл-таблицы.'}
                    </div>
                }
                color="#fff"
                zIndex={9999}
                placement="topLeft"
            >
                <Button
                    className="report-icon-btn"
                    icon={<CopyOutlined />}
                    type="default"
                    loading={copyLoading}
                    disabled={isToolsBusy}
                    onClick={isEditingModal ? handleCopyDashboardPeriod : handleCopyAllData}
                />
            </Tooltip>
        </>
    );

    // Определяем, показывать ли панели настроек
    const showSettingsPanels = !isCompactMode && !hideGroupingsPanel && !hideMetricsPanel;
    const showGroupingsPanel = !hideGroupingsPanel && displayMode === 'full';
    const showMetricsPanel = !hideMetricsPanel && displayMode === 'full';
    
    // Эффективная высота таблицы
    const effectiveTableHeight = tableHeight ?? dimensionsTable.height;
    
    // Ограничение данных: используем maxRows если передан явно, иначе без ограничения
    // В compact режиме BaseTable использует виртуальный скроллинг — рендерятся только видимые строки
    const effectiveMaxRows = maxRows;
    
    const renderTable = useMemo(() => {
        // Ограничиваем данные если указан maxRows
        let displayData = dataRef.current.data;
        if (effectiveMaxRows && displayData.length > effectiveMaxRows) {
            displayData = displayData.slice(0, effectiveMaxRows);
        }
        // «Динамика» и спарклайны: пришли данные — строки надо перерисовать. Колонки BaseTable
        // не меняются, а новый массив строк перерисует их (так же, как после загрузки данных таблицы).
        // И один раз после выключения — иначе старые подписи и линии остаются в ячейках
        const isDecorated = isAnalysis || isSparkActive || hasCellFills;
        if (isDecorated || wasDecoratedRef.current) {
            if (displayData === dataRef.current.data) {
                displayData = displayData.slice();
            }
            decorVersionRef.current += 1;
        }
        wasDecoratedRef.current = isDecorated;
        const decorRowProps = { 'data-decor': decorVersionRef.current };
        
        return (
            <div className={`universal-table${isCompactMode ? ' universal-table--compact' : ''}`} id={tableId} ref={tableDivRef} style={{ position: 'relative', ...(isCompactMode ? { marginTop: 0 } : {}) }}>
                    {/* Оверлей загрузки (скрыт в compact-режиме — там за это отвечает DashboardBlock).
                        В окне блока дашборда — показываем: пока грузятся данные за другой период
                        (предпросмотр, смена настроек), старые цифры не должны быть видны */}
                    {(isEditingModal
                        ? (ownTableLoading || effectiveTableFetching)
                        : (!isCompactMode && (isTableLoading || effectiveTableFetching))) && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(255, 255, 255, 0.75)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 100,
                                borderRadius: 4,
                            }}
                        >
                            <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 36, color: '#1890ff' }} spin />}
                            />
                            <div style={{ marginTop: 12, color: '#666', fontSize: 14 }}>
                                Загрузка данных...
                            </div>
                        </div>
                    )}
                    
                    {/* Кнопка открытия полного отчёта для compact/preview режимов */}
                    {isCompactMode && onOpenFullReport && (
                        <div 
                            style={{ 
                                position: 'absolute', 
                                top: 8, 
                                right: 8, 
                                zIndex: 10,
                            }}
                        >
                            <button
                                onClick={onOpenFullReport}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: 12,
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                                title="Открыть полный отчёт"
                            >
                                ↗
                            </button>
                        </div>
                    )}
                    
                    {showSettingsPanels && (
                    <div className="universal-table-settings" style={{ width: effectiveTableWidth }}>
                        {showGroupingsPanel && (
                        <div style={{ width: columnWidth - 10, display: 'flex', alignItems: 'center' }}>
                            {renderExportButton({ marginRight: 8 })}
                            <GroupingsComponent
                                reportType={reportType}
                                hidePersonalDataGroups={hidePersonalDataGroups}
                                adsGroupsSelected={hasAdsGroups ? adsGroupsSelected : undefined}
                                adsGroupsAvailable={hasAdsGroups ? adsGroupsAvailable : undefined}
                                setAdsGroupsAvailable={hasAdsGroups ? setAdsGroupsAvailable : undefined}
                                setAdsGroupsSelected={hasAdsGroups ? setAdsGroupsSelected : undefined}
                                blockId={isBlockViewModal ? blockId : undefined}
                            />
                            <Tooltip
                                title={
                                    <div
                                        className="page-title-tooltip-text"
                                        dangerouslySetInnerHTML={{
                                            __html: groupingsTooltip,
                                        }}
                                    ></div>
                                }
                                color="#fff"
                                zIndex={9999}
                                trigger="click"
                                overlayClassName="page-title-tooltip"
                            >
                                    <QuestionCircleOutlined
                                        style={{ color: '#ccc', fontSize: 14, marginLeft: 10 }}
                                    />
                            </Tooltip>
                        </div>
                        )}

                        {showMetricsPanel && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Space direction="horizontal">
                                <MetricsComponent
                                    reportType={reportType}
                                    adsGroupsSelected={hasAdsGroups ? adsGroupsSelected : undefined}
                                    adsGroupsAvailable={hasAdsGroups ? adsGroupsAvailable : undefined}
                                    setAdsGroupsAvailable={hasAdsGroups ? setAdsGroupsAvailable : undefined}
                                    setAdsGroupsSelected={hasAdsGroups ? setAdsGroupsSelected : undefined}
                                    blockId={isBlockViewModal ? blockId : undefined}
                                />
                                <Tooltip
                                    placement="bottom"
                                    title={
                                        <div
                                            className="page-title-tooltip-text"
                                            dangerouslySetInnerHTML={{
                                                __html: metricsTooltip,
                                            }}
                                        ></div>
                                    }
                                    color="#fff"
                                    zIndex={9999}
                                    trigger="click"
                                    overlayClassName="page-title-tooltip"
                                >
                                    <QuestionCircleOutlined
                                        style={{ color: '#ccc', fontSize: 14, marginRight: 16 }}
                                    />
                                </Tooltip>
                            </Space>
                            <Space direction="horizontal" size={8}>
                                {renderToolButtons()}
                                <Tooltip
                                    placement="bottom"
                                    title={
                                        <div
                                            className="page-title-tooltip-text"
                                            dangerouslySetInnerHTML={{
                                                __html: tableTooltip,
                                            }}
                                        ></div>
                                    }
                                    color="#fff"
                                    zIndex={9999}
                                    trigger="click"
                                    overlayClassName="page-title-tooltip"
                                >
                                    <QuestionCircleOutlined
                                        style={{ color: '#ccc', fontSize: 14 }}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                        )}
                    </div>
                    )}

                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore */}
                    <BaseTable
                        fixed
                        columns={columnsTable}
                        data={displayData}
                        rowProps={decorRowProps}
                        rowKey="pathStr2"
                        width={effectiveTableWidth}
                        maxHeight={effectiveTableHeight || dimensionsTable.height || 400}
                        minHeight={isCompactMode ? Math.min(TABLE_MIN_HEIGHT, effectiveTableHeight || dimensionsTable.height || TABLE_MIN_HEIGHT) : TABLE_MIN_HEIGHT}
                        headerHeight={
                            isCompactMode
                                ? (displayMode === 'preview' ? 90 : 60)
                                : Object.keys(filtersTable).length > 0
                                    ? HEADER_HEIGHT + 15
                                    : HEADER_HEIGHT
                        }
                        rowHeight={CELL_HEIGHT}
                        expandColumnKey="group"
                        expandIconProps={({ rowData }: { rowData: TableRowData }) => {
                            handleExpandIconProps({ rowData });

                            return ({
                                expanded: expandedRowKeys.includes(rowData.pathStr2),
                                pathStr2: rowData.pathStr2,
                                expandable: flatMode ? false : !!rowData.children,
                                loading: downloadedRowKeysSet.current.has(rowData.pathStr2) ? false : loadRowKeysSet.current.has(rowData.pathStr2),
                                downloaded: rowData.clientData && rowData.clientData.downloaded,
                                allExpanded: descendantsOfExpandedAll.has(rowData.pathStr2),
                            });
                        }}
                        onRowExpand={({ expanded, rowData, rowKey }: { expanded: boolean; rowData: TableRowData; rowKey: string }) => {
                            if (!rowData.clientData?.downloaded) {
                                if (!loadRowKeysSet.current.has(rowData.pathStr2)) {
                                    setLoadRowKeys((prev) => [...new Set([...prev, rowKey])]);
                                    if (isCompare) {
                                        loadCompareRows(rowData);
                                    } else {
                                        loadRows(rowData);
                                        if (isAnalysisRef.current) {
                                            loadAnalysisRowsRef.current(rowData);
                                        }
                                    }

                                    setMetaTableFetching({ value: false, blockId });
                                }

                            } else {
                                if (expanded)
                                    setExpandedRowKeys((prev: string[]) => [...prev, rowKey]);
                                else
                                    setExpandedRowKeys((prev: string[]) =>
                                        prev.filter((item: string) => item !== rowKey)
                                    );
                            }
                        }}
                        expandedRowKeys={expandedRowKeys}
                        components={{
                            ExpandIcon: (props) => {
                                return <ExpandIcon {...props} setExpandedAll={setExpandedAll} />;
                            }
                        }}
                        disabled={isTableLoading}
                        onColumnResizeEnd={handleColumnResizeEnd}
                        rowRenderer={({ rowData, cells }: { rowData: TableRowData; cells: React.ReactNode[] }) => {
                            const customStyle: React.CSSProperties = {
                                height: `${CELL_HEIGHT}px`,
                                display: 'flex',
                                alignItems: 'center',
                            };
                            if (rowData.group === 'empty') {
                                rowData.pathStr2 = uuidv4();
                                const parent = rowData.parentId ? findObjectById(dataRef.current.data, rowData.parentId) : null;
                                if (parent) {
                                    const parentName: string = findTopParentObjectName(displayGroupings[parent.clientData.path.length]?.name);
                                    return (
                                        <>
                                            <div className="table-row-empty" style={{ ...customStyle, paddingTop: '10px' }}>
                                                <span style={{ color: 'rgb(196,196,196', fontSize: '9px', minWidth: '120px', position: 'absolute', top: 0, left: 10 }}>{displayGroupings[parent.clientData.path.length]?.name}</span>
                                                <Empty
                                                    style={{ paddingTop: 16 }}
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description={false}
                                                    imageStyle={{ height: 27, marginBottom: 0 }}
                                                />
                                                {parentName || 'Нет данных, соответствующих условиям отчёта.'}
                                            </div>
                                        </>
                                    );
                                } else {
                                    return (
                                        <div className="table-row-empty" style={customStyle}>
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={false}
                                                imageStyle={{ height: 27, marginBottom: 0 }}
                                            />
                                            Нет данных, соответствующих условиям отчёта.
                                        </div>
                                    );
                                }
                            }
                            if (rowData.pathStr2 === 'total' || rowData.pathStr2 === 'total_choosen') {
                                return <div className="BaseTable__row BaseTable__header-row" style={customStyle}>
                                    {cells}
                                </div>;
                            }
                            return <div className="BaseTable__row" style={customStyle}>
                                {cells}
                            </div>;
                        }}
                    />
            </div>
        );
    }, [
        isAllExpandedLoading,
        isFilling,
        columnWidth,
        hidePersonalDataGroups,
        adsGroupsSelected,
        adsGroupsAvailable,
        columnsTable,
        tableWidth,
        dimensionsTable.height,
        effectiveTableHeight,
        filtersTable,
        expandedRowKeys,
        loadRowKeys,
        groupings,
        isCompare,
        isChartLoading,
        isTableLoading,
        effectiveTableFetching,
        tableId,
        tableTooltip,
        metricsTooltip,
        groupingsTooltip,
        reportType,
        hasAdsGroups,
        showSettingsPanels,
        showGroupingsPanel,
        showMetricsPanel,
        isCompactMode,
        onOpenFullReport,
        effectiveMaxRows,
        updateTrigger, // Триггер для обновления при изменении данных
        isAnalysis,
        analysisLoading,
        analysisPeriod,
        analysisData,
        analysisChosenMetrics,
        isSparkActive,
        sparkData,
        sparkChosenSeries,
        cellFills,
        ownTableLoading,
    ]);

    return (
        <>
            {renderTable}
            {/* Окно блока дашборда: кнопки в его панели «Группировки / Метрики», на тех же местах, что в отчёте */}
            {toolbarSlots?.left && createPortal(renderExportButton({ marginLeft: 16 }), toolbarSlots.left)}
            {toolbarSlots?.right && createPortal(
                <Space direction="horizontal" size={8}>{renderToolButtons()}</Space>,
                toolbarSlots.right,
            )}
        </>
    );
};

export default ReportTable;
